import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, IconButton, Avatar, Chip, Card, CardContent,
  List, TextField, FormControlLabel, Switch, Badge, CircularProgress,
  Alert, FormControl, InputLabel, Select, MenuItem, LinearProgress, Snackbar, Slider,
  useMediaQuery, useTheme, Fab, SwipeableDrawer, GlobalStyles, Dialog, DialogTitle,
  DialogContent, DialogActions, ToggleButton, ToggleButtonGroup, Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon, MyLocation as MyLocationIcon, Phone as PhoneIcon,
  Circle as CircleIcon, Close as CloseIcon, People as PeopleIcon, Navigation as NavigationIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, DragIndicator as DragIcon,
  History as HistoryIcon, GpsFixed as GpsFixedIcon, Speed as SpeedIcon,
  Timeline as TimelineIcon, PlayArrow as PlayIcon,
  Layers as LayersIcon,
  CenterFocusStrong as FollowIcon,

  Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import {
  MapContainer, TileLayer, Marker, Popup, Polyline, useMap,
  Circle as LeafletCircle, LayersControl, VectorBaseLayer,
  L, setDefaultMarkerIcon, type LeafletMap,
  useBasemap, type BasemapController, type LayersManifest,
} from '../components/map/MapLibreCompat';
// import api from '../services/api';
import { useWSRefresh, useVisibilityRefresh } from '../contexts/WebSocketContext';
import api, { mapApi, trackingApi, geocodeApi, weatherApi, TechnicianLive, LocationPoint } from '../services/api';

// The default marker is now an inline SVG pin inside MapLibreCompat — no PNGs to fetch.
setDefaultMarkerIcon(L.divIcon({
  className: 'cm', iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
  html: '<svg width="25" height="41" viewBox="0 0 25 41"><path d="M12.5 0C5.6 0 0 5.6 0 12.5 0 22 12.5 41 12.5 41S25 22 25 12.5C25 5.6 19.4 0 12.5 0z" fill="#2A81CB"/><circle cx="12.5" cy="12.5" r="4.5" fill="#fff"/></svg>',
}));

interface MapJob {
  id: number; ticket_number: string; title: string; customer_name: string | null;
  priority: string; status: string; address: string;
  latitude: number | null; longitude: number | null;
  assigned_to_id: number | null; assigned_to_name: string | null;
  assigned_to_initials: string | null; scheduled_start: string | null;
  estimated_duration_minutes: number; geocodeStatus: 'ok' | 'pending' | 'failed';
}
interface StopData {
  lat: number; lng: number; start: string; end: string;
  duration_minutes: number; point_count: number;
  order?: number; stop_type?: 'work' | 'personal' | 'unknown';
  notes?: string | null;
  nearby_ticket?: { id: number; ticket_number: string; title: string; status: string; distance_m: number } | null;
}
interface FlightPoint {
  lat: number; lng: number; altitude: number; altitude_ft: number;
  speed: number; speed_mph: number; heading: number; timestamp: string;
}
interface FlightSegment {
  takeoff: { lat: number; lng: number; time: string };
  landing: { lat: number; lng: number; time: string };
  duration_min: number; distance_miles: number;
  max_speed_mph: number; avg_speed_mph: number;
  max_altitude_ft: number; avg_altitude_ft: number;
  point_count: number; trail: FlightPoint[];
}
interface RouteSegment {
  mode: 'driving' | 'walking';
  matched: boolean;
  confidence: number;
  coords: [number, number][];   // [lat, lng] — true OSRM road geometry when matched
  start: string; end: string;
}
interface TechRoute {
  techId: number; techName: string; techColor: string;
  date: string; points: LocationPoint[]; stops: StopData[];
  routeSegments: RouteSegment[];
  flightSegments: FlightSegment[];
  clockEvents: Array<{ type: string; timestamp: string }>;
  visible: boolean; maxSpeed: number;
}

const statusColors: Record<string, string> = { available: '#4caf50', en_route: '#ff9800', on_site: '#9c27b0', offline: '#9e9e9e' };
const priorityColors: Record<string, string> = { low: '#4caf50', medium: '#2196f3', high: '#ff9800', urgent: '#f44336' };

const createJobIcon = (priority: string, assigned: boolean, techColor?: string, techInitials?: string) => {
  const bg = techColor || priorityColors[priority] || '#9e9e9e';
  const label = techInitials && assigned ? techInitials : (assigned ? '\u2713' : '');
  return L.divIcon({
    className: 'cm', iconSize: [30, 30], iconAnchor: [15, 30], popupAnchor: [0, -30],
    html: `<div style="width:30px;height:30px;background:${bg};border:3px solid white;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 5px rgba(0,0,0,.3);opacity:${assigned?1:.65}"><div style="transform:rotate(45deg);color:white;font-size:${techInitials?'8':'9'}px;font-weight:bold;text-align:center;line-height:24px">${label}</div></div>`,
  });
};

const createTechIcon = (initials: string, color: string, status: string, speedMps?: number, unit: DistUnit = "mi") => {
  const mph = (speedMps || 0) * 2.237;
  const isDriving = mph > 2;
  const spdColor = isDriving ? (mph > 65 ? '#9c27b0' : mph > 50 ? '#f44336' : mph > 30 ? '#ff9800' : '#4caf50') : '#9e9e9e';
  const w = isDriving ? 90 : 40;
  return L.divIcon({
    className: 'cm', iconSize: [w, 40], iconAnchor: [20, 20], popupAnchor: [0, -20],
    html: `<div style="display:flex;align-items:center;gap:3px"><div style="width:40px;height:40px;background:${color};border:3px solid white;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:13px;box-shadow:0 2px 8px rgba(0,0,0,.35);position:relative;flex-shrink:0">${initials}<div style="position:absolute;bottom:-2px;right:-2px;width:12px;height:12px;background:${statusColors[status]||'#9e9e9e'};border:2px solid white;border-radius:50%"></div></div>${isDriving ? `<div style="background:rgba(0,0,0,.75);color:${spdColor};font-size:11px;font-weight:700;padding:1px 5px;border-radius:8px;white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,.3)">${Math.round(unit === "km" ? mph * 1.609344 : mph)} ${unit === "km" ? "km/h" : "mph"}</div>` : ''}</div>`,
  });
};
const createStopIcon = (isJob: boolean, dur: number, order?: number) => {
  const sz = Math.max(26, Math.min(32, 20 + Math.floor(dur / 15) * 2));
  const bg = isJob ? '#7b1fa2' : '#e65100';
  const border = isJob ? '#4a148c' : '#bf360c';
  const num = order || '';
  return L.divIcon({ className: 'cm', iconSize: [sz, sz], iconAnchor: [sz/2, sz/2], popupAnchor: [0, -sz/2],
    html: `<div style="width:${sz}px;height:${sz}px;background:${bg};border:3px solid ${border};border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;color:#fff;font-size:${sz > 28 ? 13 : 11}px;font-weight:700;line-height:1">${num}</div>` });
};

function FitBoundsOnce({ points }: { points: [number, number][] }) {
  const map = useMap(); const fitted = useRef(false);
  useEffect(() => { if (fitted.current || !points.length) return; map.fitBounds(L.latLngBounds(points).pad(0.12), { maxZoom: 18 }); fitted.current = true; }, [points, map]);
  return null;
}
function SaveMapRef({ mapRef }: { mapRef: React.MutableRefObject<LeafletMap | null> }) {
  const map = useMap(); useEffect(() => { mapRef.current = map; }, [map, mapRef]); return null;
}

/** Hoists the BasemapController out of the map context so the panel (which lives OUTSIDE
 *  <MapContainer>, next to the weather panel) can drive it. */
function SaveBasemapRef({ onReady }: { onReady: (c: BasemapController | null, m: LayersManifest | null) => void }) {
  const { ctl, manifest } = useBasemap();
  useEffect(() => { onReady(ctl, manifest); }, [ctl, manifest, onReady]);
  return null;
}

// Speed color based on mph
function speedColor(speedMs: number): string {
  const mph = speedMs * 2.237;
  if (mph < 1) return '#9e9e9e';
  if (mph < 30) return '#4caf50';
  if (mph < 50) return '#ff9800';
  if (mph < 65) return '#f44336';
  return '#9c27b0';
}

// ---- Dispatcher display-unit helpers (ADDITIVE -- does not touch tracking pipeline) ----
type DistUnit = 'mi' | 'km';
type SmallUnit = 'ft' | 'm';
const MI_PER_KM = 0.621371;
const M_PER_FT = 3.280839895;
const MPH_PER_MPS = 2.237;
const KMH_PER_MPS = 3.6;
const KMH_PER_MPH = 1.609344;
function fmtDistanceMiles(miles: number, unit: DistUnit, digits = 1): string {
  return unit === "km" ? `${(miles / MI_PER_KM).toFixed(digits)} km` : `${miles.toFixed(digits)} mi`;
}
function fmtSpeedMps(mps: number, unit: DistUnit, digits = 0): string {
  return unit === "km" ? `${(mps * KMH_PER_MPS).toFixed(digits)} km/h` : `${(mps * MPH_PER_MPS).toFixed(digits)} mph`;
}
function speedValueMps(mps: number, unit: DistUnit): number {
  return unit === "km" ? mps * KMH_PER_MPS : mps * MPH_PER_MPS;
}
function fmtSpeedMph(mph: number, unit: DistUnit, digits = 0): string {
  return unit === "km" ? `${(mph * KMH_PER_MPH).toFixed(digits)} km/h` : `${digits ? mph.toFixed(digits) : mph} mph`;
}
const speedUnitLabel = (unit: DistUnit) => (unit === "km" ? "km/h" : "mph");
function fmtSmallMeters(m: number, unit: SmallUnit, digits = 0): string {
  return unit === "ft" ? `${(m * M_PER_FT).toFixed(digits)} ft` : `${m.toFixed(digits)} m`;
}
function fmtAccuracy(m: number, unit: SmallUnit): string {
  return `±${fmtSmallMeters(m, unit, 0)}`;
}
// v2.3.2 -- altitude arrives from the API in FEET. Render it in the dispatcher's
// small unit so no screen shows a raw imperial (or raw metric) number when the
// other system is selected.
function fmtAltFt(ft: number | null | undefined, unit: SmallUnit): string {
  if (ft == null || !isFinite(Number(ft))) return '—';
  const v = Number(ft);
  return unit === 'ft'
    ? `${Math.round(v).toLocaleString()} ft`
    : `${Math.round(v / M_PER_FT).toLocaleString()} m`;
}
// v2.3.2 -- weather is fetched imperial; convert when the dispatcher is metric.
function fmtWindMph(mph: number | null | undefined, unit: DistUnit, digits = 0): string {
  if (mph == null || !isFinite(Number(mph))) return '—';
  return unit === 'km' ? `${(Number(mph) * KMH_PER_MPH).toFixed(digits)} km/h` : `${Number(mph).toFixed(digits)} mph`;
}
function fmtVisMi(mi: number | null | undefined, unit: DistUnit, digits = 1): string {
  if (mi == null || !isFinite(Number(mi))) return '—';
  return unit === 'km' ? `${(Number(mi) / MI_PER_KM).toFixed(digits)} km` : `${Number(mi).toFixed(digits)} mi`;
}
// v2.3.2 -- unit-aware I/O for the route-pipeline number inputs. Values are
// stored in the unit the KEY NAME says (meters / feet / mph); the box shows and
// accepts the dispatcher's unit and converts back on the way in.
type RpUnit = 'm' | 'ft' | 'mph' | null;
function rpToDisplay(v: string, u: RpUnit, unitsSmall: SmallUnit, unitsDistance: DistUnit): string {
  if (v === '' || v == null) return '';
  const n = Number(v);
  if (!isFinite(n)) return '';
  if (u === 'm') return unitsSmall === 'ft' ? String(+(n * M_PER_FT).toFixed(2)) : String(n);
  if (u === 'ft') return unitsSmall === 'ft' ? String(n) : String(+(n / M_PER_FT).toFixed(2));
  if (u === 'mph') return unitsDistance === 'km' ? String(+(n * KMH_PER_MPH).toFixed(1)) : String(n);
  return String(n);
}
function rpToStored(v: string, u: RpUnit, unitsSmall: SmallUnit, unitsDistance: DistUnit): string {
  if (v === '' || v == null) return '';
  const n = Number(v);
  if (!isFinite(n)) return '';
  if (u === 'm') return String(unitsSmall === 'ft' ? n / M_PER_FT : n);
  if (u === 'ft') return String(unitsSmall === 'ft' ? n : n * M_PER_FT);
  if (u === 'mph') return String(unitsDistance === 'km' ? n / KMH_PER_MPH : n);
  return String(n);
}
function rpUnitLabel(u: RpUnit, unitsSmall: SmallUnit, unitsDistance: DistUnit): string {
  if (u === 'm' || u === 'ft') return unitsSmall;
  if (u === 'mph') return unitsDistance === 'km' ? 'km/h' : 'mph';
  return '';
}

// Auto-follow a technician: pan map to their position on every update.
// `paused` is set the moment the dispatcher grabs the map (see UserPanWatch) and is
// cleared by the Recenter button. While paused we still track the tech's position
// (so the button knows where to fly back to) but we never move the camera under them.
function FollowTech({ techId, technicians, zoom, paused }: { techId: number; technicians: TechnicianLive[]; zoom: number; paused: boolean }) {
  const map = useMap();
  const lastPos = useRef<string>('');

  // Invalidate size once on mount (fixes mobile rendering)
  useEffect(() => { setTimeout(() => map.invalidateSize(), 200); }, [map]);

  // Re-center whenever technician position changes
  useEffect(() => {
    if (!techId || paused) return;
    const tech = technicians.find(t => t.id === techId);
    if (!tech?.latitude || !tech?.longitude) return;
    const posKey = `${tech.latitude.toFixed(4)},${tech.longitude.toFixed(4)}`;
    if (posKey === lastPos.current) return;
    lastPos.current = posKey;
    map.flyTo([tech.latitude, tech.longitude], Math.max(map.getZoom(), zoom), { animate: true, duration: 1.5 });
  }, [techId, technicians, map, zoom, paused]);

  return null;
}

/**
 * UserPanWatch — the "the human took the wheel" detector.
 *
 * Any drag / scroll-zoom / rotate / pitch that originates from a real input device
 * carries `originalEvent`. Our own flyTo/easeTo does not. So this fires ONLY on a
 * genuine gesture, which is exactly the signal we need to drop follow mode without
 * our own follow animation cancelling itself on the first frame.
 */
function UserPanWatch({ enabled, onUserPan }: { enabled: boolean; onUserPan: () => void }) {
  const map = useMap();
  useEffect(() => {
    if (!enabled) return;
    const h = (e: any) => { if (e && e.originalEvent) onUserPan(); };
    const evs = ['dragstart', 'zoomstart', 'rotatestart', 'pitchstart'];
    evs.forEach(ev => map.on(ev, h));
    return () => { evs.forEach(ev => map.off(ev, h)); };
  }, [map, enabled, onUserPan]);
  return null;
}

// ---- FLUID marker interpolation / dead-reckoning (ADDITIVE; pure client-side visual) ----
// Smoothly glides each tech marker from its last rendered position to the newest reported
// GPS fix over a short window, and (optionally) dead-reckons along the last heading/speed
// between fixes. Does NOT change GPS sampling or push rate and issues NO network calls --
// it only reads the already-fetched `technicians` positions. Uses ONE shared rAF loop for
// all markers (not one per marker) and stops itself when everything is settled.
const INTERP_TWEEN_MS = 1000;      // ease-to-actual duration (configurable)
const DR_MAX_MS = 6000;            // cap: stop predicting after this long with no new fix
const DR_MIN_SPEED_MPS = 1.0;      // below ~2.2mph treat as stationary (never drift)

interface InterpState {
  curLat: number; curLng: number;
  startLat: number; startLng: number;
  targetLat: number; targetLng: number;
  tweenStart: number; fixTime: number;
  headingRad: number; speedMps: number;
}
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

function useInterpolatedTechPositions(
  technicians: TechnicianLive[],
  drEnabled: boolean,
): Map<number, [number, number]> {
  const statesRef = useRef<Map<number, InterpState>>(new Map());
  const rafRef = useRef<number | null>(null);
  const drRef = useRef(drEnabled);
  drRef.current = drEnabled;
  const [positions, setPositions] = useState<Map<number, [number, number]>>(new Map());

  const ensureLoop = useCallback(() => {
    if (rafRef.current != null) return;
    const step = () => {
      const now = performance.now();
      const states = statesRef.current;
      let active = false;
      const next = new Map<number, [number, number]>();
      states.forEach((st, id) => {
        // Baseline: ease from where the marker visually is toward the true fix.
        const tw = Math.min(1, (now - st.tweenStart) / INTERP_TWEEN_MS);
        const e = easeOutCubic(tw);
        let lat = st.startLat + (st.targetLat - st.startLat) * e;
        let lng = st.startLng + (st.targetLng - st.startLng) * e;
        if (tw < 1) active = true;
        // Dead-reckoning: after reaching the fix, keep gliding along heading/speed (bounded,
        // disabled when ~stationary so a parked truck never wanders).
        if (drRef.current && st.speedMps >= DR_MIN_SPEED_MPS) {
          const elapsed = Math.min(now - st.fixTime, DR_MAX_MS) / 1000;
          const predSecs = elapsed - INTERP_TWEEN_MS / 1000; // only past the tween window
          if (predSecs > 0) {
            const dist = st.speedMps * predSecs; // metres
            const dLat = (dist * Math.cos(st.headingRad)) / 111320;
            const dLng = (dist * Math.sin(st.headingRad)) / (111320 * Math.cos(st.targetLat * Math.PI / 180));
            lat = st.targetLat + dLat;
            lng = st.targetLng + dLng;
            if ((now - st.fixTime) < DR_MAX_MS) active = true;
          }
        }
        st.curLat = lat; st.curLng = lng;
        next.set(id, [lat, lng]);
      });
      setPositions(next);
      if (active) { rafRef.current = requestAnimationFrame(step); }
      else { rafRef.current = null; }
    };
    rafRef.current = requestAnimationFrame(step);
  }, []);

  // Ingest newest reported fixes -> (re)arm tweens. Runs on every `technicians` change
  // (WS push + poll); reads props only, never fetches.
  useEffect(() => {
    const now = performance.now();
    const states = statesRef.current;
    const seen = new Set<number>();
    for (const t of technicians) {
      if (t.latitude == null || t.longitude == null) continue;
      seen.add(t.id);
      const headingRad = ((t.heading ?? 0) * Math.PI) / 180;
      const speedMps = t.speed ?? 0;
      const prev = states.get(t.id);
      if (!prev) {
        states.set(t.id, {
          curLat: t.latitude, curLng: t.longitude,
          startLat: t.latitude, startLng: t.longitude,
          targetLat: t.latitude, targetLng: t.longitude,
          tweenStart: now, fixTime: now, headingRad, speedMps,
        });
      } else if (prev.targetLat !== t.latitude || prev.targetLng !== t.longitude) {
        // New fix: ease from wherever the marker currently is (incl. any dead-reckoning)
        // to the truth -> no teleport, and prediction can't accumulate error.
        prev.startLat = prev.curLat; prev.startLng = prev.curLng;
        prev.targetLat = t.latitude; prev.targetLng = t.longitude;
        prev.tweenStart = now; prev.fixTime = now;
        prev.headingRad = headingRad; prev.speedMps = speedMps;
      } else {
        prev.headingRad = headingRad; prev.speedMps = speedMps; // refresh DR inputs only
      }
    }
    for (const id of Array.from(states.keys())) if (!seen.has(id)) states.delete(id);
    ensureLoop();
  }, [technicians, ensureLoop]);

  useEffect(() => () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current); }, []);

  return positions;
}

export default function MapPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [jobs, setJobs] = useState<MapJob[]>([]);
  const [technicians, setTechnicians] = useState<TechnicianLive[]>([]);
  const [loading, setLoading] = useState(true);
  const [geocodingProgress, setGeocodingProgress] = useState({ active: false, done: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [technicianFilter, setTechnicianFilter] = useState<string>('all');
  const [selectedTechIds, setSelectedTechIds] = useState<number[]>([]);
  const [showUnassigned, setShowUnassigned] = useState(true);
  const [showAssigned, setShowAssigned] = useState(true);
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState('');
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [ticketForm, setTicketForm] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success'|'warning'|'error' });
  const mapRef = useRef<LeafletMap | null>(null);
  const [followTechId, setFollowTechId] = useState<number | 0>(0);
  const [followZoom, setFollowZoom] = useState(17);
  // Follow is PAUSED (not cancelled) when the dispatcher pans/zooms by hand. The
  // Recenter button un-pauses it. Cancelling outright would lose which tech they
  // were watching, which is the thing they actually care about.
  const [followPaused, setFollowPaused] = useState(false);

  // ── Layer switcher (Street / Topo / Satellite + 3D, hillshade, contours) ──
  // Availability is NOT hardcoded — it comes from /map-style/layers.json at runtime, so
  // Topo and Satellite light up the moment their tilesets land, with no frontend rebuild.
  const basemapCtl = useRef<BasemapController | null>(null);
  const [layersManifest, setLayersManifest] = useState<LayersManifest | null>(null);
  const [activeBasemap, setActiveBasemap] = useState('street');
  const [activeOverlays, setActiveOverlays] = useState<string[]>([]);
  const [mapTheme, setMapTheme] = useState<'day' | 'night'>('day');
  const onBasemapReady = useCallback((c: BasemapController | null, m: LayersManifest | null) => {
    basemapCtl.current = c; setLayersManifest(m);
  }, []);
  const pickBasemap = (id: string) => { basemapCtl.current?.setBasemap(id); setActiveBasemap(id); };
  const toggleOverlay = (id: string) => {
    const on = !activeOverlays.includes(id);
    basemapCtl.current?.setOverlay(id, on);
    setActiveOverlays(p => on ? [...p, id] : p.filter(x => x !== id));
  };
  const toggleTheme = () => {
    const t = mapTheme === 'day' ? 'night' : 'day';
    void basemapCtl.current?.setTheme(t); setMapTheme(t);
  };
  const [liveRefreshInterval] = useState(15);

  // ---- Dispatcher display units + low-accuracy uncertainty shading (ADDITIVE) ----
  const [unitsDistance, setUnitsDistance] = useState<DistUnit>(() => (localStorage.getItem("dispatch_units_distance") as DistUnit) || "mi");
  const [unitsSmall, setUnitsSmall] = useState<SmallUnit>(() => (localStorage.getItem("dispatch_units_small") as SmallUnit) || "m");
  const [showUncertainty, setShowUncertainty] = useState<boolean>(() => { const v = localStorage.getItem("dispatch_uncertainty"); return v === null ? true : v === "1"; });
  const [uncertaintyThreshold, setUncertaintyThreshold] = useState<number>(30);
  // FLUID marker glide / dead-reckoning toggle (default ON, conservative). Visual only.
  const [deadReckoning, setDeadReckoning] = useState<boolean>(() => { const v = localStorage.getItem('dispatch_deadreckon'); return v === null ? true : v === '1'; });
  const interpPositions = useInterpolatedTechPositions(technicians, deadReckoning);

  // v2.3.2 -- fleet defaults for the map display knobs (system_settings gps_map_*).
  // A dispatcher's local choice (localStorage) still wins; this just seeds it.
  useEffect(() => {
    (async () => {
      try {
        const r = await trackingApi.getGpsSettings();
        const m: Record<string, string> = {};
        (r.data.settings || []).forEach((x: any) => { m[x.key] = String(x.value ?? ''); });
        if (m.gps_map_uncertainty_threshold_m) setUncertaintyThreshold(Number(m.gps_map_uncertainty_threshold_m) || 30);
        if (localStorage.getItem('dispatch_uncertainty') === null && m.gps_map_uncertainty_enabled)
          setShowUncertainty(m.gps_map_uncertainty_enabled === 'true');
        if (localStorage.getItem('dispatch_deadreckon') === null && m.gps_map_interpolation_enabled)
          setDeadReckoning(m.gps_map_interpolation_enabled === 'true');
        if (localStorage.getItem('dispatch_units_distance') === null && m.gps_map_units_distance)
          setUnitsDistance((m.gps_map_units_distance === 'km' ? 'km' : 'mi') as DistUnit);
        if (localStorage.getItem('dispatch_units_small') === null && m.gps_map_units_small)
          setUnitsSmall((m.gps_map_units_small === 'ft' ? 'ft' : 'm') as SmallUnit);
      } catch {}
    })();
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/tracker-api/api/config", { credentials: "omit" });
        if (!r.ok) return;
        const cfg = await r.json();
        // Device agent may expose keys either at top level or nested under `prefs`.
        const th = cfg?.low_accuracy_threshold_m ?? cfg?.prefs?.low_accuracy_threshold_m;
        if (typeof th === "number" && th > 0) setUncertaintyThreshold(th);
        // Keep shading ON by default on the dispatcher side; the dispatcher toggle (persisted) is authoritative.
      } catch { /* default gracefully */ }
    })();
  }, []);

  // Layers panel
  const [layersPanelOpen, setLayersPanelOpen] = useState(false);
  const [gpsSettingsOpen, setGpsSettingsOpen] = useState(false);
  // v2.3.11 — the basemap is SELF-HOSTED. It used to be CARTO's CDN, which meant a
  // third party saw every dispatcher's viewport (i.e. where our techs are) in real
  // time. Tiles now come from our own nginx at /tiles/{z}/{x}/{y}.png, same origin.
  const [mapTileSettings, setMapTileSettings] = useState<any>({ default_tile: 'concept', google_enabled: false, google_tiles_enabled: false, thirdparty_enabled: false, available_tiles: {}, effective_tile: 'concept', user_preference: null, allowed_tiles: ['concept'] });
  const [mapSettingsOpen, setMapSettingsOpen] = useState(false);
  const [gpsSettings, setGpsSettings] = useState<Record<string, string>>({});
  const [gpsSettingsMeta, setGpsSettingsMeta] = useState<any[]>([]);
  const [gpsSettingsLoading, setGpsSettingsLoading] = useState(false);
  // Layer opacity: 0 = off, 0.01-1.0 = on with opacity
  const [radarOpacity, setRadarOpacity] = useState(0);
  const [rainviewerUrl, setRainviewerUrl] = useState('');
  const [precipOpacity, setPrecipOpacity] = useState(0);
  const [tempOpacity, setTempOpacity] = useState(0);
  const [windOpacity, setWindOpacity] = useState(0);
  const [cloudsOpacity, setCloudsOpacity] = useState(0);
  const [pressureOpacity, setPressureOpacity] = useState(0);
  // v2.3.11 PRIVACY: this was a GOOGLE traffic tile layer (mt{0-3}.google.com/vt),
  // hardcoded and ON by default at 0.6 opacity. It was quietly sending Google the
  // dispatch map's viewport — every tile, every pan — which is the exact leak the
  // no-third-party rule forbids, and it survived the CARTO removal because nobody
  // was looking for it. It is now OFF by default and only renders at all when an
  // admin has explicitly enabled third-party tiles (map_thirdparty_tiles_enabled).
  // We have no traffic feed of our own — OSRM gives us the road graph, not live
  // congestion — so this is honestly a capability we drop rather than fake.
  const [trafficOpacity, setTrafficOpacity] = useState(0);
  const [tileUrls, setTileUrls] = useState<Record<string, string>>({});
  const [weatherData, setWeatherData] = useState<any>(null);
  const [nwsAlerts, setNwsAlerts] = useState<any[]>([]);
  const [alertsOpacity, setAlertsOpacity] = useState(0.7);
  const [selectedAlert, setSelectedAlert] = useState<any>(null);

  // Routes per tech
  const [techRoutes, setTechRoutes] = useState<Record<number, TechRoute>>({});
  const [routeDate, setRouteDate] = useState(new Date().toISOString().split('T')[0]);

  // Address search & directions state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{ display_name: string; lat: string; lon: string }>>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [directionsRoute, setDirectionsRoute] = useState<[number, number][] | null>(null);
  const [directionsInfo, setDirectionsInfo] = useState<{ distance: string; duration: string; steps: Array<{ instruction: string; distance: string; }> } | null>(null);
  const [directionsTarget, setDirectionsTarget] = useState<{ lat: number; lng: number; name: string } | null>(null);
  const [showDirectionsPanel, setShowDirectionsPanel] = useState(false);

  // Address search — PROXIED THROUGH OUR OWN BACKEND (v2.3.12).
  // This used to call https://nominatim.openstreetmap.org DIRECTLY from the
  // browser, which handed a third party every address a dispatcher typed plus
  // the office IP. It now goes to /api/navigation/geocode; the SERVER talks to
  // Nominatim (throttled, identified, cached). The browser talks only to us.
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchAbortRef = useRef<AbortController | null>(null);
  const searchAddress = useCallback((q: string) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (searchAbortRef.current) searchAbortRef.current.abort();
    if (q.length < 3) { setSearchResults([]); return; }
    searchTimeoutRef.current = setTimeout(async () => {
      const abort = new AbortController();
      searchAbortRef.current = abort;
      try {
        const r = await api.get('/navigation/geocode', {
          params: { q, limit: 6 }, signal: abort.signal as any,
        });
        if (!abort.signal.aborted) {
          const data = (r.data?.results || []).map((x: any) => ({
            display_name: x.label, lat: String(x.lat), lon: String(x.lng),
          }));
          setSearchResults(data);
          setSearchOpen(true);
        }
      } catch (e: any) { if (e.name !== 'AbortError' && e.code !== 'ERR_CANCELED') setSearchResults([]); }
    }, 350);
  }, []);

  // Get directions from OUR OWN routing engine (v2.3.12).
  // This used to call router.project-osrm.org — a THIRD-PARTY server that then
  // knew where our techs and our customers are. It now calls
  // /api/navigation/route, the authenticated proxy in front of Concept's
  // self-hosted OSRM (the same engine the tracker app navigates with).
  const getDirections = async (fromLat: number, fromLng: number, toLat: number, toLng: number, targetName: string) => {
    try {
      const r = await api.get('/navigation/route', {
        params: { from: `${fromLat},${fromLng}`, to: `${toLat},${toLng}` },
      });
      const data = r.data;
      if (data && data.ok !== false && Array.isArray(data.geometry) && data.geometry.length > 1) {
        const coords: [number, number][] = data.geometry.map((c: number[]) => [c[0], c[1]]);
        setDirectionsRoute(coords);
        const distStr = fmtDistanceMiles(data.distance_m / 1609.34, unitsDistance, 1);
        const durMin = Math.round(data.duration_s / 60);
        const steps = (data.steps || []).map((s: any) => ({
          instruction: [s.type, s.modifier, s.name].filter(Boolean).join(' ') || 'Continue',
          distance: (s.distance_m / 1609.34) < 0.1 ? fmtSmallMeters(s.distance_m, unitsSmall, 0) : fmtDistanceMiles(s.distance_m / 1609.34, unitsDistance, 1),
        })).filter((s: any) => s.instruction);
        setDirectionsInfo({ distance: distStr, duration: `${durMin} min`, steps });
        setDirectionsTarget({ lat: toLat, lng: toLng, name: targetName });
        setShowDirectionsPanel(true);
        // Fit map to route
        if (mapRef.current && coords.length > 1) {
          mapRef.current.fitBounds(L.latLngBounds(coords).pad(0.1));
        }
      }
    } catch (e) { console.error('Directions failed:', e); }
  };

  // Navigate to destination using native maps app
  const openInMaps = (lat: number, lng: number, name?: string) => {
    const dest = `${lat},${lng}`;
    // Try Google Maps first (works on Android), fallback to Apple Maps
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('android')) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`, '_blank');
    } else if (ua.includes('iphone') || ua.includes('ipad')) {
      window.open(`https://maps.apple.com/?daddr=${dest}&dirflg=d`, '_blank');
    } else {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`, '_blank');
    }
  };

  const clearDirections = () => {
    setDirectionsRoute(null);
    setDirectionsInfo(null);
    setDirectionsTarget(null);
    setShowDirectionsPanel(false);
  };
  const [routeLoading, setRouteLoading] = useState<number | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTechId, setHistoryTechId] = useState<number | null>(null);
  const [historyDate, setHistoryDate] = useState(new Date().toISOString().split('T')[0]);
  const [autoLoadedToday, setAutoLoadedToday] = useState(false);
  const [expandedTechs, setExpandedTechs] = useState<Record<number, boolean>>({});
  const [dragJob, setDragJob] = useState<{id: number, techId: number, idx: number} | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const fetchMapData = useCallback(async (silent = false) => {
    try {
      if (!silent) { setLoading(true); setError(null); }
      const resp = await mapApi.getData(false);
      const mappedJobs: MapJob[] = resp.data.jobs.map((j: any) => ({ ...j, geocodeStatus: j.latitude && j.longitude ? 'ok' : 'pending' }));
      setJobs(mappedJobs); setTechnicians(resp.data.technicians || []); setLastRefresh(new Date());
      const needsGeo = mappedJobs.filter((j: MapJob) => j.geocodeStatus === 'pending' && j.address !== 'No address');
      if (needsGeo.length > 0) {
        setGeocodingProgress({ active: true, done: 0, total: needsGeo.length });
        let done = 0;
        for (const job of needsGeo) {
          try { const gr = await geocodeApi.geocodeTicket(job.id); if (gr.data.success) { job.latitude = gr.data.latitude; job.longitude = gr.data.longitude; job.geocodeStatus = 'ok'; } else job.geocodeStatus = 'failed'; } catch { job.geocodeStatus = 'failed'; }
          done++; setGeocodingProgress({ active: true, done, total: needsGeo.length });
        }
        setJobs([...mappedJobs]); setGeocodingProgress({ active: false, done: 0, total: 0 });
      }
    } catch (err) { setError(err instanceof Error ? err.message : 'Failed to load'); } finally { setLoading(false); }
  }, []);

  const refreshLive = useCallback(async () => {
    try {
      const r = await trackingApi.getLiveLocations();
      const techs = r.data.technicians || [];
      setTechnicians(techs);
      setLastRefresh(new Date());
    } catch {}
  }, []);
  const refreshLiveRef = useRef(refreshLive);
  useEffect(() => { refreshLiveRef.current = refreshLive; }, [refreshLive]);
  // Mirrored into a ref so the WS push handler (which closes over an older render)
  // reads the CURRENT pause state, not the one from whenever it was created.
  const followPausedRef = useRef(followPaused);
  useEffect(() => { followPausedRef.current = followPaused; }, [followPaused]);

  // Recenter: un-pause follow and snap back to whoever we were watching. On mobile
  // with no explicit follow target, fall back to the first tech reporting GPS.
  const handleRecenter = useCallback(() => {
    setFollowPaused(false);
    const tech = technicians.find(t => t.id === followTechId)
      || (isMobile ? technicians.find(t => t.latitude && t.longitude) : undefined);
    if (tech?.latitude && tech?.longitude && mapRef.current) {
      mapRef.current.flyTo([tech.latitude, tech.longitude], Math.max(mapRef.current.getZoom(), followZoom), { animate: true, duration: 1.0 });
    }
  }, [technicians, followTechId, followZoom, isMobile]);

  // Is follow actually armed? (a tech is selected, or mobile auto-follow is in play)
  const followArmed = followTechId > 0 || (isMobile && technicians.some(t => t.latitude && t.longitude));

  const loadRoute = useCallback(async (tech: TechnicianLive, date: string) => {
    setRouteLoading(tech.id);
    try {
      const resp = await trackingApi.getLocationHistory(tech.id, { date });
      const d = resp.data;
      const speeds = (d.points || []).map((p: LocationPoint) => (p.speed || 0));
      const maxSpd = speeds.length ? Math.max(...speeds) : 0;
      const flights: FlightSegment[] = d.flight_segments || [];
      setTechRoutes(prev => ({ ...prev, [tech.id]: {
        techId: tech.id, techName: tech.name, techColor: tech.color,
        date, points: d.points || [], stops: d.stops || [],
        routeSegments: (d.route_segments || []) as RouteSegment[],
        flightSegments: flights,
        clockEvents: d.clock_events || [], visible: true, maxSpeed: maxSpd,
      }}));
      // Fit bounds to ground points only (not airplane points)
      const groundPts = d.points || [];
      if (groundPts.length > 0 && mapRef.current) {
        const lats = groundPts.map((p: LocationPoint) => p.lat);
        const lngs = groundPts.map((p: LocationPoint) => p.lng);
        mapRef.current.fitBounds([[Math.min(...lats)-.01,Math.min(...lngs)-.01],[Math.max(...lats)+.01,Math.max(...lngs)+.01]]);
      } else if (flights.length > 0 && mapRef.current) {
        // Only flight data - zoom to show flight arc
        const allLats = flights.flatMap(f => [f.takeoff.lat, f.landing.lat]);
        const allLngs = flights.flatMap(f => [f.takeoff.lng, f.landing.lng]);
        mapRef.current.fitBounds([[Math.min(...allLats)-.5,Math.min(...allLngs)-.5],[Math.max(...allLats)+.5,Math.max(...allLngs)+.5]]);
      }
      const flightMsg = flights.length > 0 ? `, ${flights.length} flight${flights.length > 1 ? 's' : ''}` : '';
      const segs: RouteSegment[] = d.route_segments || [];
      const drv = segs.filter(s => s.mode === 'driving');
      const matched = drv.filter(s => s.matched).length;
      const snapMsg = drv.length ? `, ${matched}/${drv.length} drive legs road-matched` : '';
      setSnackbar({ open: true, message: `${d.points.length} pts, ${(d.stops||[]).length} stops${snapMsg}${flightMsg}`, severity: 'success' });
    } catch { setSnackbar({ open: true, message: 'Failed to load route', severity: 'error' }); }
    finally { setRouteLoading(null); }
  }, []);

  useEffect(() => {
    trackingApi.getMapSettings().then(r => { if (r.data) setMapTileSettings(r.data); }).catch(() => {});
  }, []);

  

  const loadGpsSettings = useCallback(async () => {
    setGpsSettingsLoading(true);
    try {
      const r = await trackingApi.getGpsSettings();
      const map: Record<string, string> = {};
      (r.data.settings || []).forEach((s: any) => { map[s.key] = String(s.value ?? ''); });
      setGpsSettings(map);
      setGpsSettingsMeta(r.data.settings || []);   // v2.3.2: type/unit/min/max/desc
    } catch {}
    finally { setGpsSettingsLoading(false); }
  }, []);

  const saveGpsSetting = useCallback(async (key: string, value: string) => {
    setGpsSettings(prev => ({ ...prev, [key]: value }));
    try { await trackingApi.updateGpsSettings({ [key]: value }); }
    catch { setSnackbar({ open: true, message: 'Failed to save setting', severity: 'error' }); }
  }, []);

  const toggleRouteVisible = (techId: number) => {
    setTechRoutes(prev => prev[techId] ? { ...prev, [techId]: { ...prev[techId], visible: !prev[techId].visible } } : prev);
  };
  const removeRoute = (techId: number) => {
    setTechRoutes(prev => { const n = { ...prev }; delete n[techId]; return n; });
  };

  
  // WebSocket push — instant refresh on ticket changes
  useWSRefresh(['ticket:assigned', 'ticket:updated', 'ticket:status', 'ticket:created', 'ticket:deleted'], () => { fetchMapData(true); });
  // Separate handler for GPS location updates (lightweight - just refreshes tech positions)
  // Instant GPS push - update tech positions in-place from WS data, then background refresh
  useWSRefresh(['tracking:location'], (data: any) => {
    if (data?.updated_users?.length) {
      setTechnicians(prev => {
        const updated = [...prev];
        for (const u of data.updated_users) {
          const idx = updated.findIndex(t => t.id === u.user_id);
          if (idx >= 0) {
            updated[idx] = {
              ...updated[idx],
              latitude: u.latitude,
              longitude: u.longitude,
              speed: u.speed ?? updated[idx].speed,
              accuracy: u.accuracy ?? updated[idx].accuracy,
              last_location_update: u.timestamp ?? new Date().toISOString(),
              status: (u.speed > 0.5) ? 'en_route' : updated[idx].status === 'offline' ? 'available' : updated[idx].status,
            };
          }
        }
        return updated;
      });
      setLastRefresh(new Date());
    }
    // Auto-center map on updated tech position (mobile or when following).
    // followPausedRef: once the dispatcher has grabbed the map, NOTHING moves the
    // camera until they hit Recenter. This is the whole point of the feature.
    if (data?.updated_users?.length && mapRef.current && !followPausedRef.current) {
      const u = data.updated_users[0];
      if (followTechId && u.user_id === followTechId) {
        mapRef.current.setView([u.latitude, u.longitude], Math.max(mapRef.current.getZoom(), 13), { animate: true, duration: 0.8 });
      } else if (isMobile && !followTechId && u.latitude && u.longitude) {
        // On mobile with no follow set, gently re-center if zoomed in enough
        if (mapRef.current.getZoom() >= 8) {
          mapRef.current.setView([u.latitude, u.longitude], mapRef.current.getZoom(), { animate: true, duration: 1 });
        }
      }
    }
    // Also do a lightweight API refresh every 3rd push to sync full state
    if (Math.random() < 0.33) refreshLiveRef.current();
  });

  useEffect(() => { fetchMapData(); const iv = setInterval(() => fetchMapData(true), 30000); return () => clearInterval(iv); }, [fetchMapData]);
  useEffect(() => { const i = setInterval(() => refreshLiveRef.current(), liveRefreshInterval * 1000); return () => clearInterval(i); }, [liveRefreshInterval]);
  // Refresh immediately when tab becomes visible (browsers throttle intervals in background tabs)
  useVisibilityRefresh(() => { 
    refreshLiveRef.current(); 
    fetchMapData(true);
    // Force snackbar if we were stale
    const age = Math.floor((Date.now() - lastRefresh.getTime()) / 1000);
    if (age > 60) setSnackbar({ open: true, message: `Resuming — data was ${Math.floor(age/60)}m behind`, severity: 'warning' });
  });

  // Auto-load today's route
  useEffect(() => {
    if (!autoLoadedToday && technicians.length > 0 && Object.keys(techRoutes).length === 0) {
      const firstWithGps = technicians.find(t => t.latitude && t.longitude);
      if (firstWithGps) {
        loadRoute(firstWithGps, new Date().toISOString().split('T')[0]);
        // On mobile, auto-follow first tech with GPS and zoom to them
        if (isMobile && !followTechId && mapRef.current) {
          setFollowTechId(firstWithGps.id);
          mapRef.current.setView([firstWithGps.latitude!, firstWithGps.longitude!], 13, { animate: true });
        }
      }
      setAutoLoadedToday(true);
    }
  }, [technicians, autoLoadedToday, techRoutes, loadRoute, isMobile, followTechId]);

  // Fetch weather tile URLs and current conditions
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const [tilesResp, alertsResp, weatherResp, radarResp] = await Promise.all([
          weatherApi.getTileUrls(),
          weatherApi.getAlerts(),
          weatherApi.getCurrent(39.1, -121.1),
          weatherApi.getRadar(),
        ]);
        if (tilesResp.data) setTileUrls(tilesResp.data);
        if (alertsResp.data) setNwsAlerts(alertsResp.data.alerts || []);
        if (weatherResp.data) setWeatherData(weatherResp.data);
        if (radarResp.data?.tile_url) setRainviewerUrl(radarResp.data.tile_url);
      } catch (e) { console.error('Weather fetch error:', e); }
    };
    fetchWeather();
    const interval = setInterval(fetchWeather, 300000); // 5min
    return () => clearInterval(interval);
  }, []);

  const handleCenterMap = () => {
    const pts: [number, number][] = [
      ...jobs.filter(j => j.latitude && j.longitude).map(j => [j.latitude!, j.longitude!] as [number, number]),
      ...technicians.filter(t => t.latitude && t.longitude).map(t => [t.latitude!, t.longitude!] as [number, number]),
    ];
    if (pts.length && mapRef.current) mapRef.current.fitBounds(L.latLngBounds(pts).pad(0.1));
  };


  const openNavigation = (lat: number, lng: number, label?: string) => {
    // Detect platform and open best navigation app
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    const dest = `${lat},${lng}`;
    
    if (isAndroid) {
      // Google Maps intent on Android
      window.open(`google.navigation:q=${dest}`, '_blank');
      // Fallback to Google Maps web
      setTimeout(() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`, '_blank'), 500);
    } else if (isIOS) {
      // Apple Maps
      window.open(`maps://maps.apple.com/?daddr=${dest}&dirflg=d`, '_blank');
    } else {
      // Desktop - open Google Maps
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`, '_blank');
    }
  };

  const openNavigationFromCurrent = (destLat: number, destLng: number) => {
    // Try to use current tech position as origin
    const tech = technicians.find(t => t.id === followTechId) || technicians.find(t => t.latitude);
    const isAndroid = /Android/.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const dest = `${destLat},${destLng}`;
    
    if (isAndroid) {
      window.open(`google.navigation:q=${dest}`, '_blank');
    } else if (isIOS) {
      window.open(`maps://maps.apple.com/?daddr=${dest}&dirflg=d`, '_blank');
    } else {
      const origin = tech?.latitude && tech?.longitude ? `${tech.latitude},${tech.longitude}` : '';
      window.open(`https://www.google.com/maps/dir/?api=1${origin ? '&origin=' + origin : ''}&destination=${dest}&travelmode=driving`, '_blank');
    }
  };

    // Close search when clicking map
  const handleMapClick = () => { setSearchOpen(false); };

  const handleLocateTech = (tech: TechnicianLive) => { if (tech.latitude && tech.longitude && mapRef.current) mapRef.current.setView([tech.latitude, tech.longitude], 16); };

  const openTicketDialog = async (jobId: number) => {
    try {
      const r = await api.get(`/tickets/${jobId}`);
      const t = r.data;
      setSelectedTicket(t);
      setTicketForm({
        title: t.title || '', description: t.description || '', status: t.status || 'new',
        priority: t.priority || 'medium', assigned_to_id: t.assigned_to_id || '',
        scheduled_start: t.scheduled_start ? t.scheduled_start.slice(0, 16) : '',
        scheduled_end: t.scheduled_end ? t.scheduled_end.slice(0, 16) : '',
        notes: '',
      });
      setTicketDialogOpen(true);
    } catch { setSnackbar({ open: true, message: 'Failed to load ticket', severity: 'error' }); }
  };

  const saveTicket = async () => {
    if (!selectedTicket) return;
    setSaving(true);
    try {
      const payload: any = {};
      if (ticketForm.title !== selectedTicket.title) payload.title = ticketForm.title;
      if (ticketForm.description !== selectedTicket.description) payload.description = ticketForm.description;
      if (ticketForm.priority !== selectedTicket.priority) payload.priority = ticketForm.priority;
      if (ticketForm.status !== selectedTicket.status) payload.status = ticketForm.status;
      if (Object.keys(payload).length > 0) await api.put(`/tickets/${selectedTicket.id}`, payload);
      if (ticketForm.assigned_to_id && ticketForm.assigned_to_id !== selectedTicket.assigned_to_id) {
        await api.post(`/tickets/${selectedTicket.id}/assign`, {
          assigned_to_id: ticketForm.assigned_to_id,
          scheduled_start: ticketForm.scheduled_start ? new Date(ticketForm.scheduled_start).toISOString() : undefined,
          scheduled_end: ticketForm.scheduled_end ? new Date(ticketForm.scheduled_end).toISOString() : undefined,
        });
      }
      setSnackbar({ open: true, message: 'Ticket updated', severity: 'success' });
      setTicketDialogOpen(false); fetchMapData();
    } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.detail || 'Save failed', severity: 'error' }); }
    finally { setSaving(false); }
  };

  const _inDate = (j: MapJob) => {
    if (dateFrom && j.scheduled_start) {
      const d = j.scheduled_start.split('T')[0];
      if (d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
    }
    return true;
  };
  // Technicians that actually have work assigned within the current date window.
  const techsWithWork = new Set<number>(
    jobs.filter(j => j.assigned_to_id && _inDate(j)).map(j => j.assigned_to_id as number)
  );
  // Which technicians are visible: null => all; otherwise a restricted set.
  const allowedTechIds: Set<number> | null =
    selectedTechIds.length > 0 ? new Set(selectedTechIds)
    : technicianFilter === 'all' ? null
    : technicianFilter === 'assigned' ? techsWithWork
    : new Set([parseInt(technicianFilter)]);
  const techVisible = (id?: number | null) => id != null && (allowedTechIds === null || allowedTechIds.has(id));

  const filteredJobs = jobs.filter(j => {
    if (!_inDate(j)) return false;
    const isAssigned = !!j.assigned_to_id;
    if (isAssigned && !showAssigned) return false;
    if (!isAssigned && !showUnassigned) return false;
    if (allowedTechIds !== null) {
      // A restricted tech view only shows that/those tech(s) assigned work.
      if (!j.assigned_to_id || !allowedTechIds.has(j.assigned_to_id)) return false;
    }
    return true;
  });
  const mappableJobs = filteredJobs.filter(j => j.latitude && j.longitude);
  const allPts: [number, number][] = [
    ...mappableJobs.map(j => [j.latitude!, j.longitude!] as [number, number]),
    ...technicians.filter(t => t.latitude && t.longitude).map(t => [t.latitude!, t.longitude!] as [number, number]),
  ];
  const getTimeSince = (d: string | null) => { if (!d) return 'N/A'; const s = Math.floor((Date.now()-new Date(d).getTime())/1000); return s<60?'Just now':s<3600?`${Math.floor(s/60)}m ago`:`${Math.floor(s/3600)}h ago`; };
  const [tick, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick(n => n + 1), 5000); return () => clearInterval(t); }, []);
  const refreshAge = Math.floor((Date.now() - lastRefresh.getTime()) / 1000);
  const isStale = refreshAge > 90;
  // Inject pulse animation CSS
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = '@keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.3 } }';
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  // Haversine distance in miles
  const haversine = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 3959;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  };

  // Filter out close-proximity points (GPS jitter when stationary)
  const filterProximity = (points: LocationPoint[], minDistFeet: number = 30): LocationPoint[] => {
    if (points.length < 2) return points;
    const filtered: LocationPoint[] = [points[0]];
    for (let i = 1; i < points.length; i++) {
      const last = filtered[filtered.length - 1];
      const dist = haversine(last.lat, last.lng, points[i].lat, points[i].lng) * 5280; // miles to feet
      // Keep point if it's far enough away OR if it's the last point
      if (dist >= minDistFeet || i === points.length - 1) {
        filtered.push(points[i]);
      }
    }
    return filtered;
  };

  // Build speed-colored polyline segments (breaks at flight gaps, filters jitter)
  const getSpeedSegments = (points: LocationPoint[], _color?: string): { positions: [number, number][]; color: string }[] => {
    const cleaned = filterProximity(points, 80);
    if (cleaned.length < 2) return [];
    const segments: { positions: [number, number][]; color: string }[] = [];
    let currentColor = speedColor(cleaned[0].speed || 0);
    let currentPositions: [number, number][] = [[cleaned[0].lat, cleaned[0].lng]];
    
    for (let i = 1; i < cleaned.length; i++) {
      const dist = haversine(cleaned[i-1].lat, cleaned[i-1].lng, cleaned[i].lat, cleaned[i].lng);
      const timeDiff = (new Date(cleaned[i].timestamp).getTime() - new Date(cleaned[i-1].timestamp).getTime()) / 1000;
      const impliedMph = timeDiff > 0 ? (dist / timeDiff) * 3600 : 0;
      
      // Break segment if gap > 2 miles and implied speed > 200mph (flight gap)
      if (dist > 2 && impliedMph > 200) {
        if (currentPositions.length >= 2) {
          segments.push({ positions: [...currentPositions], color: currentColor });
        }
        currentColor = speedColor(cleaned[i].speed || 0);
        currentPositions = [[cleaned[i].lat, cleaned[i].lng]];
        continue;
      }
      
      const c = speedColor(cleaned[i].speed || 0);
      currentPositions.push([cleaned[i].lat, cleaned[i].lng]);
      if (c !== currentColor || i === cleaned.length - 1) {
        segments.push({ positions: [...currentPositions], color: currentColor });
        currentColor = c;
        currentPositions = [[cleaned[i].lat, cleaned[i].lng]];
      }
    }
    return segments;
  };

  // Generate direction arrow markers along a route
  const getDirectionArrows = (points: LocationPoint[], techColor: string): { lat: number; lng: number; angle: number }[] => {
    const cleaned = filterProximity(points, 100);
    if (cleaned.length < 2) return [];
    const arrows: { lat: number; lng: number; angle: number }[] = [];
    // Place an arrow every ~8-15 points depending on density
    const step = Math.max(4, Math.floor(cleaned.length / 12));
    for (let i = step; i < cleaned.length; i += step) {
      const prev = cleaned[i - 1];
      const curr = cleaned[i];
      // Calculate bearing
      const dLng = (curr.lng - prev.lng) * Math.PI / 180;
      const lat1 = prev.lat * Math.PI / 180;
      const lat2 = curr.lat * Math.PI / 180;
      const y = Math.sin(dLng) * Math.cos(lat2);
      const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
      const bearing = (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
      // Only add arrow if there's actual movement (not stationary jitter)
      const dist = haversine(prev.lat, prev.lng, curr.lat, curr.lng) * 5280;
      if (dist > 20) {
        arrows.push({ lat: curr.lat, lng: curr.lng, angle: bearing });
      }
    }
    return arrows;
  };

  const getJobsForTech = (techId: number) => {
    return filteredJobs
      .filter(j => j.assigned_to_id === techId)
      .sort((a, b) => (a.scheduled_start || '').localeCompare(b.scheduled_start || ''));
  };
  const toggleTechExpand = (techId: number) => {
    setExpandedTechs(prev => ({ ...prev, [techId]: !prev[techId] }));
  };
  const handleJobDragStart = (jobId: number, techId: number, idx: number) => {
    setDragJob({ id: jobId, techId, idx });
  };
  const handleJobDragOverSlot = (e: React.DragEvent, idx: number) => {
    e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverIdx(idx);
  };
  const handleJobDrop = async (e: React.DragEvent, targetIdx: number, techId: number) => {
    e.preventDefault(); setDragOverIdx(null);
    if (!dragJob || dragJob.techId !== techId) { setDragJob(null); return; }
    const techJobs = getJobsForTech(techId);
    if (dragJob.idx === targetIdx) { setDragJob(null); return; }
    const reordered = [...techJobs];
    const [moved] = reordered.splice(dragJob.idx, 1);
    reordered.splice(targetIdx, 0, moved);
    const starts = techJobs.map(j => j.scheduled_start).sort();
    try {
      for (let i = 0; i < reordered.length; i++) {
        if (reordered[i].scheduled_start !== starts[i]) {
          await api.patch('/tickets/' + reordered[i].id, { scheduled_start: starts[i] || '' });
        }
      }
      fetchMapData(true);
    } catch {}
    setDragJob(null);
  };
  const handleJobDragEnd = () => { setDragJob(null); setDragOverIdx(null); };

  const TechPanel = () => (
    <>
      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={600}>Technicians ({technicians.length})</Typography>
          <Typography variant="caption" color={isStale ? 'warning.main' : 'text.secondary'}>
            GPS • {isStale ? `⚠ ${Math.floor(refreshAge/60)}m stale` : `${liveRefreshInterval}s • ${lastRefresh.toLocaleTimeString([], {hour:'2-digit',minute:'2-digit',second:'2-digit'})}`}
          </Typography>
        </Box>
        {isMobile && <IconButton onClick={() => setMobileDrawerOpen(false)} size="small"><CloseIcon /></IconButton>}
      </Box>
      <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <TextField type="date" label="Route date" value={routeDate} onChange={(e) => setRouteDate(e.target.value)}
          InputLabelProps={{ shrink: true }} size="small" fullWidth sx={{ mb: 0.5 }} />
      </Box>
      <List sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {technicians.filter(t => techVisible(t.id)).map((tech) => {
          const route = techRoutes[tech.id];
          return (
          <Card key={tech.id} elevation={0} sx={{ mb: 1, border: '1px solid', borderColor: route ? 'primary.main' : 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Badge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={<CircleIcon sx={{ fontSize: 10, color: statusColors[tech.status] }} />}>
                  <Avatar sx={{ bgcolor: tech.color, width: 36, height: 36, fontSize: 13 }}>{tech.initials}</Avatar>
                </Badge>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{tech.name}</Typography>
                    <Chip label={tech.status} size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: statusColors[tech.status], color: 'white' }} />
                    {tech.time_state && tech.time_state !== 'idle' && tech.time_state !== 'off_clock' && (
                      <Chip label={tech.time_state === 'onsite' ? '\u23F1 On Site' : tech.time_state === 'traveling' ? '\u{1F697} Travel' : '\u23F8 Paused'}
                        size="small" sx={{ height: 18, fontSize: '0.55rem', ml: 0.5,
                        bgcolor: tech.time_state === 'onsite' ? '#e8f5e9' : tech.time_state === 'traveling' ? '#e3f2fd' : '#fff3e0',
                        color: tech.time_state === 'onsite' ? '#2e7d32' : tech.time_state === 'traveling' ? '#1565c0' : '#e65100',
                        fontWeight: 700 }} />
                    )}
                  </Box>
                  {tech.latitude && (
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.3 }}>
                      <GpsFixedIcon sx={{ fontSize: 11, color: 'success.main' }} />
                      <Typography variant="caption" color="text.secondary">{getTimeSince(tech.last_location_update)}</Typography>
                      {tech.speed != null && tech.speed > 0 && (
                        <Typography variant="caption"><SpeedIcon sx={{ fontSize: 11 }} /> {fmtSpeedMps(tech.speed, unitsDistance, 0)}</Typography>
                      )}
                    </Box>
                  )}
                  {tech.time_state && tech.time_state !== 'idle' && tech.time_state !== 'off_clock' && tech.time_since && (
                    <Box sx={{ mt: 0.3 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                        {tech.time_state === 'onsite' ? 'Working' : tech.time_state === 'traveling' ? 'En route' : 'Away'}: {(() => {
                          const mins = Math.floor((Date.now() - new Date(tech.time_since).getTime()) / 60000);
                          return mins >= 60 ? `${Math.floor(mins/60)}h ${mins%60}m` : `${mins}m`;
                        })()}
                      </Typography>
                    </Box>
                  )}
                  {route && (
                    <Box sx={{ mt: 0.5, p: 0.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" display="block">
                        Max: <strong>{fmtSpeedMps(route.maxSpeed, unitsDistance, 0)}</strong> • {route.points.length} pts • {route.stops.filter(s => s.stop_type === 'work').length}🔧 {route.stops.filter(s => s.stop_type !== 'work').length}📍 stops
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" sx={{ fontSize: '0.6rem', minWidth: 0, px: 0.8, py: 0.3 }}
                  onClick={() => { handleLocateTech(tech); if (isMobile) setMobileDrawerOpen(false); }} disabled={!tech.latitude}>
                  <MyLocationIcon sx={{ fontSize: 13 }} />
                </Button>
                <Button size="small" variant={followTechId === tech.id ? "contained" : "outlined"}
                  color={followTechId === tech.id ? "primary" : "inherit"}
                  sx={{ fontSize: '0.6rem', minWidth: 0, px: 0.8, py: 0.3, textTransform: 'none' }}
                  onClick={() => {
                    setFollowTechId(followTechId === tech.id ? 0 : tech.id);
                    setFollowPaused(false);
                    if (followTechId !== tech.id && tech.latitude && tech.longitude && mapRef.current) {
                      mapRef.current.setView([tech.latitude, tech.longitude], followZoom, { animate: true });
                    }
                    if (isMobile) setMobileDrawerOpen(false);
                  }} disabled={!tech.latitude}>
                  <FollowIcon sx={{ fontSize: 13 }} />
                </Button>
                {!route ? (
                  <Button size="small" variant="contained" color="primary" sx={{ fontSize: '0.6rem', px: 1, py: 0.3, textTransform: 'none' }}
                    disabled={routeLoading === tech.id}
                    startIcon={routeLoading === tech.id ? <CircularProgress size={12} /> : <TimelineIcon sx={{ fontSize: 13 }} />}
                    onClick={() => loadRoute(tech, routeDate)}>
                    Load Route
                  </Button>
                ) : (
                  <>
                    <Button size="small" variant={route.visible ? "contained" : "outlined"} color="secondary"
                      sx={{ fontSize: '0.6rem', px: 0.8, py: 0.3, textTransform: 'none' }}
                      onClick={() => toggleRouteVisible(tech.id)}
                      startIcon={route.visible ? <VisibilityIcon sx={{ fontSize: 13 }} /> : <VisibilityOffIcon sx={{ fontSize: 13 }} />}>
                      Route
                    </Button>
                    <Button size="small" variant="outlined" color="error" sx={{ fontSize: '0.6rem', minWidth: 0, px: 0.8, py: 0.3 }}
                      onClick={() => removeRoute(tech.id)}>
                      <CloseIcon sx={{ fontSize: 13 }} />
                    </Button>
                  </>
                )}
                <Button size="small" variant="outlined" sx={{ fontSize: '0.6rem', px: 1, py: 0.3, textTransform: 'none' }}
                  onClick={() => { setHistoryTechId(tech.id); setHistoryDate(routeDate); setHistoryOpen(true); }}>
                  <HistoryIcon sx={{ fontSize: 13 }} />
                </Button>
              </Box>
            </CardContent>
            {(() => {
              const techJobs = getJobsForTech(tech.id);
              if (!techJobs.length) return null;
              const isExp = expandedTechs[tech.id];
              return (
                <>
                  <Box sx={{ borderTop: '1px solid', borderColor: 'divider', px: 1.5, py: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}
                    onClick={() => toggleTechExpand(tech.id)}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      {techJobs.length} job{techJobs.length !== 1 ? 's' : ''} assigned
                    </Typography>
                    {isExp ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                  </Box>
                  {isExp && (
                    <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
                      {techJobs.map((job, idx) => (
                        <Box key={job.id} draggable
                          onDragStart={() => handleJobDragStart(job.id, tech.id, idx)}
                          onDragOver={(e: React.DragEvent) => handleJobDragOverSlot(e, idx)}
                          onDrop={(e: React.DragEvent) => handleJobDrop(e, idx, tech.id)}
                          onDragEnd={handleJobDragEnd}
                          onClick={(e) => { e.stopPropagation(); navigate('/tickets?id=' + job.id); }}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.6,
                            borderBottom: '1px solid', borderColor: 'divider', cursor: 'pointer',
                            bgcolor: dragOverIdx === idx && dragJob?.techId === tech.id ? 'rgba(25,118,210,0.12)' : 'transparent',
                            opacity: dragJob?.id === job.id ? 0.35 : 1,
                            '&:hover': { bgcolor: 'action.hover' },
                          }}>
                          <DragIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'grab', flexShrink: 0 }} />
                          <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: tech.color, color: 'white', fontSize: 8, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {idx + 1}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" fontWeight={600} noWrap display="block" sx={{ fontSize: 11, lineHeight: 1.2 }}>{job.title}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ fontSize: 9 }}>
                              {job.scheduled_start ? new Date(job.scheduled_start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'Unscheduled'}
                              {job.customer_name ? ' \u2022 ' + job.customer_name : ''}
                            </Typography>
                          </Box>
                          <Chip label={job.priority[0].toUpperCase()} size="small"
                            sx={{ height: 16, fontSize: 8, fontWeight: 700, minWidth: 0, bgcolor: priorityColors[job.priority], color: 'white', '& .MuiChip-label': { px: 0.5 } }} />
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              );
            })()}
          </Card>
          );
        })}
        {!technicians.length && <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>No tracked technicians</Typography>}
      </List>
    </>
  );

  return (
    <>
      <GlobalStyles styles={{
        '.leaflet-control-layers': { borderRadius: '8px !important', border: 'none !important', boxShadow: '0 2px 6px rgba(0,0,0,.2) !important' },
        '.leaflet-control-layers-toggle': { width: '32px !important', height: '32px !important', backgroundSize: '20px !important' },
        '.leaflet-control-zoom': { borderRadius: '8px !important', border: 'none !important', boxShadow: '0 2px 6px rgba(0,0,0,.2) !important' },
        '.leaflet-control-zoom a': { width: '32px !important', height: '32px !important', lineHeight: '32px !important' },
        '.cm': { background: 'transparent !important', border: 'none !important' },
      }} />
      <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Top bar */}
        <Paper elevation={0} sx={{ px: { xs: 1, md: 2 }, py: 0.8, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 }, flexWrap: 'nowrap', flexShrink: 0, overflow: 'hidden' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mr: 0.5, display: { xs: 'none', md: 'block' } }}>Map</Typography>
          <FormControl size="small" sx={{ minWidth: { xs: 90, md: 120 } }}>
            <InputLabel>Technician</InputLabel>
            <Select value={technicianFilter} label="Technician" onChange={(e) => { setTechnicianFilter(e.target.value); setSelectedTechIds([]); }} sx={{ height: 32 }}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="assigned">All with work</MenuItem>
              {technicians.map(t => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120, display: { xs: 'none', md: 'inline-flex' } }}>
            <InputLabel>Select techs</InputLabel>
            <Select multiple value={selectedTechIds} label="Select techs" sx={{ height: 32 }}
              onChange={(e) => { const v = e.target.value as number[]; setSelectedTechIds(typeof v === 'string' ? [] : v); }}
              renderValue={(sel) => (sel as number[]).length === 0 ? 'None' : `${(sel as number[]).length} selected`}>
              {technicians.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: 90, md: 130 } }}>
            <InputLabel>Follow</InputLabel>
            <Select value={followTechId} label="Follow" onChange={(e) => {
              setFollowPaused(false);
              const id = Number(e.target.value);
              setFollowTechId(id);
              if (id) {
                const t = technicians.find(x => x.id === id);
                if (t?.latitude && t?.longitude && mapRef.current) mapRef.current.setView([t.latitude, t.longitude], followZoom, { animate: true });
              }
            }} sx={{ height: 32 }}
              startAdornment={followTechId ? <FollowIcon sx={{ fontSize: 16, color: 'primary.main', mr: 0.5 }} /> : undefined}>
              <MenuItem value={0}><em>None</em></MenuItem>
              {technicians.filter(t => t.latitude).map(t => (
                <MenuItem key={t.id} value={t.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Avatar sx={{ bgcolor: t.color, width: 20, height: 20, fontSize: 9 }}>{t.initials}</Avatar>
                    {t.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel control={<Switch size="small" checked={showAssigned} onChange={(e) => setShowAssigned(e.target.checked)} />}
            label={<Typography variant="caption">Assigned</Typography>} sx={{ ml: 0, display: { xs: 'none', md: 'flex' } }}/>
          <FormControlLabel control={<Switch size="small" checked={showUnassigned} onChange={(e) => setShowUnassigned(e.target.checked)} />}
            label={<Typography variant="caption">Unassigned</Typography>} sx={{ mr: 0, display: { xs: 'none', md: 'flex' } }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              style={{ height: 28, fontSize: 11, border: '1px solid #ccc', borderRadius: 4, padding: '0 4px' }} />
            <Box component="span" sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">to</Typography>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="today"
                style={{ height: 28, fontSize: 11, border: '1px solid #ccc', borderRadius: 4, padding: '0 4px' }} />
            </Box>
          </Box>

          {/* Dispatcher display units (distance + small) -- persisted in localStorage */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5, ml: 1 }}>
            <FormControl size="small" sx={{ minWidth: 66 }}>
              <InputLabel>Dist</InputLabel>
              <Select value={unitsDistance} label="Dist" sx={{ height: 32 }}
                onChange={(e) => { const v = e.target.value as DistUnit; setUnitsDistance(v); localStorage.setItem('dispatch_units_distance', v); }}>
                <MenuItem value="mi">mi</MenuItem>
                <MenuItem value="km">km</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 60 }}>
              <InputLabel>Alt</InputLabel>
              <Select value={unitsSmall} label="Alt" sx={{ height: 32 }}
                onChange={(e) => { const v = e.target.value as SmallUnit; setUnitsSmall(v); localStorage.setItem('dispatch_units_small', v); }}>
                <MenuItem value="m">m</MenuItem>
                <MenuItem value="ft">ft</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <FormControlLabel
            control={<Switch size="small" checked={showUncertainty}
              onChange={(e) => { setShowUncertainty(e.target.checked); localStorage.setItem('dispatch_uncertainty', e.target.checked ? '1' : '0'); }} />}
            label={<Typography variant="caption">±Acc</Typography>} sx={{ ml: 0, display: { xs: 'none', md: 'flex' } }} />
          <FormControlLabel
            control={<Switch size="small" checked={deadReckoning}
              onChange={(e) => { setDeadReckoning(e.target.checked); localStorage.setItem('dispatch_deadreckon', e.target.checked ? '1' : '0'); }} />}
            label={<Typography variant="caption">Glide</Typography>} sx={{ ml: 0, display: { xs: 'none', md: 'flex' } }} />

          <Box sx={{ flex: 1 }} />
          <Chip label={`${technicians.filter(t => t.latitude).length} live`} size="small" icon={<GpsFixedIcon sx={{ fontSize: 13 }} />} color="success" variant="outlined" sx={{ display: { xs: 'none', sm: 'flex' } }} />
          <Chip label={`${mappableJobs.length} jobs`} size="small" variant="outlined" sx={{ display: { xs: 'none', sm: 'flex' } }} />
          <IconButton size="small" onClick={() => setLayersPanelOpen(!layersPanelOpen)} color={layersPanelOpen ? 'primary' : 'default'}>
            <LayersIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleCenterMap}><MyLocationIcon fontSize="small" /></IconButton>
          <IconButton size="small" onClick={() => fetchMapData()} disabled={loading}>
            {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Paper>

        {geocodingProgress.active && <LinearProgress variant="determinate" value={(geocodingProgress.done/geocodingProgress.total)*100} sx={{ flexShrink: 0 }} />}
        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ flexShrink: 0 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* Sidebar */}
          <Paper elevation={0} sx={{ width: 260, display: { xs: 'none', md: 'flex' }, borderRight: '1px solid', borderColor: 'divider', flexDirection: 'column', flexShrink: 0 }}>
            <TechPanel />
          </Paper>

          {/* Map area */}
          <Box sx={{ flex: 1, position: 'relative' }}>
  
          {/* Scrolling NWS Advisory Banner */}
          {nwsAlerts.length > 0 && alertsOpacity > 0 && (
            <Box id="advisoryBanner" sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1000, bgcolor: 'rgba(255,152,0,0.95)', color: 'white', overflow: 'hidden', height: 28, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => setSelectedAlert(nwsAlerts[0])}>
              <Box sx={{ display: 'flex', animation: 'scroll-left 45s linear infinite', whiteSpace: 'nowrap', '@keyframes scroll-left': { '0%': { transform: 'translateX(0%)' }, '100%': { transform: 'translateX(-50%)' } } }}>
                {[...nwsAlerts, ...nwsAlerts].map((a, i) => (
                  <Typography key={i} variant="caption" sx={{ mx: 4, fontWeight: 600, cursor: 'pointer', letterSpacing: 0.3 }}
                    onClick={(e) => { e.stopPropagation(); setSelectedAlert(a); }}>
                    ⚠️ {a.event}: {a.headline?.substring(0, 150)}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
          {/* Address Search Bar */}
          <Box sx={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 1100, width: { xs: '85%', md: 380 } }}>
            <Paper sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.3, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,.15)' }}>
              <TextField size="small" placeholder="Search address..." variant="standard"
                value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); searchAddress(e.target.value); }}
                onFocus={() => setSearchOpen(true)}
                InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 1 } }}
                sx={{ flex: 1 }} />
              {searchQuery && <IconButton size="small" onClick={() => { setSearchQuery(''); setSearchResults([]); setSearchOpen(false); }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>}
            </Paper>
            {searchOpen && searchResults.length > 0 && (
              <Paper sx={{ mt: 0.5, maxHeight: 200, overflow: 'auto', borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,.15)' }}>
                {searchResults.map((r, i) => (
                  <Box key={i} sx={{ px: 1.5, py: 1, cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #f0f0f0',
                    '&:hover': { bgcolor: '#f5f5f5' } }}
                    onClick={() => {
                      const lat = parseFloat(r.lat); const lng = parseFloat(r.lon);
                      if (mapRef.current) mapRef.current.flyTo([lat, lng], 17, { animate: true, duration: 1 });
                      setSearchOpen(false); setSearchQuery(r.display_name.split(',').slice(0,2).join(','));
                      // Offer directions from nearest tech
                      const nearest = technicians.find(t => t.latitude && t.longitude);
                      if (nearest?.latitude && nearest?.longitude) {
                        getDirections(nearest.latitude, nearest.longitude, lat, lng, r.display_name.split(',')[0]);
                      }
                    }}>
                    {r.display_name}
                  </Box>
                ))}
              </Paper>
            )}
          </Box>

          {/* Directions Panel */}
          {showDirectionsPanel && directionsInfo && (
            <Paper sx={{ position: 'absolute', bottom: 80, left: 8, zIndex: 1100, width: { xs: 'calc(100% - 16px)', md: 320 },
              maxHeight: '40vh', overflow: 'auto', borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,.2)' }}>
              <Box sx={{ p: 1.5, bgcolor: '#1976d2', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '8px 8px 0 0' }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{directionsInfo.distance} • {directionsInfo.duration}</Typography>
                  <Typography sx={{ fontSize: 11, opacity: 0.9 }}>{directionsTarget?.name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {directionsTarget && (
                    <IconButton size="small" sx={{ color: 'white' }}
                      onClick={() => openInMaps(directionsTarget.lat, directionsTarget.lng, directionsTarget.name)}>
                      <NavigationIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                  <IconButton size="small" sx={{ color: 'white' }} onClick={clearDirections}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ p: 1 }}>
                {directionsInfo.steps.map((step, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, py: 0.5, borderBottom: '1px solid #f0f0f0', alignItems: 'flex-start' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1976d2', minWidth: 20, textAlign: 'right' }}>{i + 1}</Typography>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 12 }}>{step.instruction}</Typography>
                      <Typography sx={{ fontSize: 10, color: '#999' }}>{step.distance}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}

          <MapContainer center={[39.0, -121.1]} zoom={9} maxZoom={22} style={{ height: '100%', width: '100%' }}>
              <SaveMapRef mapRef={mapRef} />
              <SaveBasemapRef onReady={onBasemapReady} />
              <FitBoundsOnce points={allPts} />
              <UserPanWatch enabled={followArmed && !followPaused} onUserPan={() => setFollowPaused(true)} />
              {followTechId > 0 && <FollowTech techId={followTechId} technicians={technicians} zoom={followZoom} paused={followPaused} />}

              {isStale && (
                <div style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 1000,
                  background: '#ff9800', color: '#fff', padding: '4px 12px', borderRadius: 16, fontSize: 11, fontWeight: 600,
                  boxShadow: '0 2px 8px rgba(0,0,0,.2)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                  onClick={() => { refreshLiveRef.current(); setSnackbar({ open: true, message: 'Refreshing...', severity: 'success' }); }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', animation: 'pulse 1.5s infinite' }} />
                  Position {Math.floor(refreshAge/60)}m old — tap to refresh
                </div>
              )}
              <LayersControl position="topright" collapsed={true}>
                {/* v2.3.13 — the self-hosted base is now a VECTOR style (/map-style/concept-v1.json,
                    tiles from /tiles-gl/). One tileset serves every zoom instead of a fresh PNG per
                    tile per zoom, it overzooms cleanly past z14 where the raster path went to mush,
                    and it's ~5-8x less data over the wire. Nothing leaves our origin. */}
                <LayersControl.BaseLayer checked={(mapTileSettings?.effective_tile || 'concept') === 'concept'} name="Concept (self-hosted, vector)">
                  <VectorBaseLayer />
                </LayersControl.BaseLayer>
                {/* Third-party raster bases remain available ONLY when an admin has explicitly
                    turned them on. Default is off; default is ours. */}
                {mapTileSettings?.thirdparty_enabled && Object.entries(mapTileSettings?.available_tiles || {})
                  .filter(([key]: [string, any]) => key !== 'concept')
                  .map(([key, tile]: [string, any]) => (
                    <LayersControl.BaseLayer key={key} checked={key === mapTileSettings.effective_tile} name={tile.name}>
                      <TileLayer attribution={`&copy; ${tile.attr}`} url={tile.url} maxZoom={tile.maxZoom || 19} />
                    </LayersControl.BaseLayer>
                  ))}
              </LayersControl>

              {/* Weather radar overlay */}
              {radarOpacity > 0 && rainviewerUrl && (
                <TileLayer url={rainviewerUrl} opacity={radarOpacity} />
              )}
              {precipOpacity > 0 && tileUrls.precipitation && (
                <TileLayer url={tileUrls.precipitation} opacity={precipOpacity} />
              )}
              {tempOpacity > 0 && tileUrls.temperature && (
                <TileLayer url={tileUrls.temperature} opacity={tempOpacity} />
              )}
              {windOpacity > 0 && tileUrls.wind && (
                <TileLayer url={tileUrls.wind} opacity={windOpacity} />
              )}
              {cloudsOpacity > 0 && tileUrls.clouds && (
                <TileLayer url={tileUrls.clouds} opacity={cloudsOpacity} />
              )}
              {pressureOpacity > 0 && tileUrls.pressure && (
                <TileLayer url={tileUrls.pressure} opacity={pressureOpacity} />
              )}
              {trafficOpacity > 0 && mapTileSettings?.thirdparty_enabled && (
                <TileLayer url="https://{s}.google.com/vt/lyrs=m@221097413,traffic&x={x}&y={y}&z={z}" maxZoom={20} subdomains={['mt0','mt1','mt2','mt3']} opacity={trafficOpacity} />
              )}

              {/* NWS Alert markers */}
              {alertsOpacity > 0 && nwsAlerts.filter(a => a.polygon).map((alert, i) => (
                <Polyline key={`alert-${i}`}
                  positions={alert.polygon.map((c: number[]) => [c[1], c[0]] as [number, number])}
                  pathOptions={{
                    color: alert.severity === 'Extreme' ? '#f44336' : alert.severity === 'Severe' ? '#ff9800' : '#ffeb3b',
                    weight: 3, fillOpacity: 0.15 * alertsOpacity, fill: true, opacity: alertsOpacity,
                    dashArray: alert.severity === 'Extreme' ? undefined : '6 4',
                  }}
                  eventHandlers={{ click: () => setSelectedAlert(alert) }}
                >
                  <Popup maxWidth={350}>
                    <div style={{ maxWidth: 320 }}>
                      <strong style={{ color: alert.severity === 'Extreme' ? '#f44336' : alert.severity === 'Severe' ? '#ff9800' : '#ed6c02' }}>
                        ⚠️ {alert.event}
                      </strong><br/>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>{alert.severity} • {alert.urgency}</span><br/>
                      <span style={{ fontSize: 12 }}>{alert.headline}</span><br/>
                      {alert.instruction && <div style={{ marginTop: 4, padding: 4, background: '#fff3e0', borderRadius: 4, fontSize: 11 }}>{alert.instruction}</div>}
                      <span style={{ fontSize: 11, color: '#666' }}>Expires: {alert.expires ? new Date(alert.expires).toLocaleString() : 'Unknown'}</span><br/>
                      {alert.areas && <span style={{ fontSize: 11, color: '#666' }}>Areas: {alert.areas.substring(0, 150)}</span>}
                    </div>
                  </Popup>
                </Polyline>
              ))}

              {/* Job markers */}
              {mappableJobs.map(job => (
                <Marker key={`j-${job.id}`} position={[job.latitude!, job.longitude!]} icon={createJobIcon(job.priority, !!job.assigned_to_id, job.assigned_to_id ? (technicians.find(t => t.id === job.assigned_to_id)?.color) : undefined, job.assigned_to_initials || undefined)}>
                  <Popup maxWidth={250}>
                    <div style={{ minWidth: 180 }}>
                      <strong>{job.ticket_number}</strong><br/>{job.title}<br/>
                      <span style={{ color: '#666', fontSize: 12 }}>{job.customer_name}<br/>{job.address}</span><br/>
                      <span style={{ color: priorityColors[job.priority], fontWeight: 600, fontSize: 12 }}>{job.priority.toUpperCase()}</span>
                      {job.assigned_to_name && <span style={{ fontSize: 12 }}> • {job.assigned_to_name}</span>}<br/>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                        <button onClick={() => openTicketDialog(job.id)} style={{ padding: '4px 10px', cursor: 'pointer', border: '1px solid #1976d2', background: 'white', color: '#1976d2', borderRadius: 4, fontSize: 11 }}>Ticket</button>
                        <button onClick={() => {
                          const tech = technicians.find(t => t.id === job.assigned_to_id && t.latitude && t.longitude) || technicians.find(t => t.latitude && t.longitude);
                          if (tech?.latitude && tech?.longitude && job.latitude && job.longitude) getDirections(tech.latitude, tech.longitude, job.latitude, job.longitude, job.ticket_number + ' - ' + job.address);
                        }} style={{ padding: '4px 10px', cursor: 'pointer', border: '1px solid #4caf50', background: '#4caf50', color: 'white', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>Directions</button>
                        <button onClick={() => { if (job.latitude && job.longitude) openInMaps(job.latitude, job.longitude, job.address); }}
                          style={{ padding: '4px 10px', cursor: 'pointer', border: 'none', background: '#1976d2', color: 'white', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>Navigate ↗</button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}

              {/* Tech markers */}
              {technicians.filter(t => t.latitude && t.longitude && techVisible(t.id)).map(tech => (
                <Marker key={`t-${tech.id}`} position={interpPositions.get(tech.id) ?? [tech.latitude!, tech.longitude!]} icon={createTechIcon(tech.initials, tech.color, tech.status, tech.speed ?? undefined, unitsDistance)}>
                  <Popup maxWidth={260}>
                    <div style={{ minWidth: 200 }}>
                      <strong>{tech.name}</strong> <span style={{ color: statusColors[tech.status], fontSize: 12 }}>({tech.status})</span>
                      {tech.activity_state && <span style={{ fontSize: 10, color: '#666', marginLeft: 4 }}>{tech.activity_state}</span>}
                      <br/>
                      {tech.phone && <span style={{ fontSize: 12 }}><PhoneIcon sx={{ fontSize: 11 }} /> {tech.phone}<br/></span>}
                      <div style={{ margin: '4px 0', padding: '4px 6px', background: '#f5f5f5', borderRadius: 4, fontSize: 11 }}>
                        {tech.speed != null && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Speed:</span><strong style={{ color: speedColor(tech.speed) }}>{fmtSpeedMps(tech.speed, unitsDistance, 1)}</strong></div>}
                        {tech.heading != null && (tech.speed ?? 0) > 0.5 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Heading:</span><span>{tech.heading.toFixed(0)}°</span></div>}
                        {tech.altitude_ft != null && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Altitude:</span><span>{fmtAltFt(tech.altitude_ft, unitsSmall)}</span></div>}
                        {tech.accuracy != null && tech.accuracy > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Accuracy:</span><span style={{ color: tech.accuracy > uncertaintyThreshold ? "#1976d2" : undefined }}>{fmtAccuracy(tech.accuracy, unitsSmall)}</span></div>}
                    {tech.latitude && tech.longitude && <button onClick={() => openNavigation(tech.latitude!, tech.longitude!)} style={{ marginTop: 6, padding: '4px 10px', cursor: 'pointer', border: 'none', background: '#1976d2', color: 'white', borderRadius: 4, fontSize: 11, fontWeight: 600, width: '100%' }}>Navigate to Tech 🧭</button>}
                        {tech.battery_level != null && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Battery:</span><span style={{ color: tech.battery_level < 20 ? '#f44336' : tech.battery_level < 50 ? '#ff9800' : '#4caf50' }}>{tech.battery_level}%{tech.battery_state === 'charging' ? ' ⚡' : ''}</span></div>}
                      </div>
                      <span style={{ fontSize: 11, color: '#666' }}>Updated: {getTimeSince(tech.last_location_update)}</span><br/>
                      {tech.current_job_title && <span style={{ fontSize: 11, color: '#9c27b0', fontWeight: 600 }}>Job: {tech.current_job_title}<br/></span>}
                      <span style={{ fontSize: 11 }}>Today: {tech.jobs_completed_today}/{tech.jobs_today} jobs</span>
                      {tech.steps_today != null && <span style={{ fontSize: 11, color: '#666' }}> • {tech.steps_today.toLocaleString()} steps</span>}
                    </div>
                  </Popup>
                </Marker>
              ))}

              {/* LOW-ACCURACY uncertainty circles -- LIVE technicians (radius = accuracy in meters). */}
              {showUncertainty && technicians.filter(t => t.latitude && t.longitude && techVisible(t.id) && (t.accuracy ?? 0) > uncertaintyThreshold).map(tech => (
                <LeafletCircle key={`unc-t-${tech.id}`} center={interpPositions.get(tech.id) ?? [tech.latitude!, tech.longitude!]} radius={tech.accuracy!}
                  pathOptions={{ color: '#2196f3', fillColor: '#2196f3', fillOpacity: 0.12, weight: 1, opacity: 0.4 }} interactive={false} />
              ))}

              {/* LOW-ACCURACY uncertainty circles -- HISTORICAL route points (only the worse-than-threshold ones). */}
              {showUncertainty && Object.values(techRoutes).filter(r => r.visible && r.points.length > 0).map(route => (
                route.points.filter(p => ((p as any).accuracy ?? 0) > uncertaintyThreshold).map((p, i) => (
                  <LeafletCircle key={`unc-r-${route.techId}-${i}`} center={[p.lat, p.lng]} radius={(p as any).accuracy}
                    pathOptions={{ color: '#2196f3', fillColor: '#2196f3', fillOpacity: 0.12, weight: 1, opacity: 0.3 }} interactive={false} />
                ))
              ))}

              {/* Route polylines per tech.
                  PRIMARY: route_segments — the actual OSRM-matched ROAD geometry.
                  Driving legs that matched are drawn solid and follow real streets.
                  Driving legs outside OSRM coverage are drawn dashed (honest raw GPS).
                  Walking/stationary is drawn dotted and is never snapped to a road.
                  NOTE: do NOT run these through filterProximity — decimating the
                  geometry is what used to flatten curves and cut corners. */}
              {Object.values(techRoutes).filter(r => r.visible && (r.routeSegments?.length ?? 0) > 0).map(route =>
                route.routeSegments.filter(s => s.coords && s.coords.length > 1).map((seg, i) => (
                  <Polyline
                    key={`rt-${route.techId}-${i}`}
                    positions={seg.coords}
                    pathOptions={
                      seg.mode === 'driving'
                        ? (seg.matched
                            ? { color: route.techColor, weight: 5, opacity: 0.95 }
                            : { color: route.techColor, weight: 4, opacity: 0.6, dashArray: '10 8' })
                        : { color: '#7e8a97', weight: 3, opacity: 0.55, dashArray: '2 6' }
                    }
                  >
                    <Popup>
                      <span style={{ fontSize: 12 }}>
                        <strong>{seg.mode === 'driving' ? (seg.matched ? 'Drive (road-matched)' : 'Drive (no road data — raw GPS)') : 'Walking / stationary'}</strong><br/>
                        {new Date(seg.start).toLocaleTimeString()} – {new Date(seg.end).toLocaleTimeString()}<br/>
                        {seg.matched && `match confidence ${(seg.confidence * 100).toFixed(0)}%`}
                      </span>
                    </Popup>
                  </Polyline>
                ))
              )}

              {/* Legacy fallback: only when the backend returned no route_segments */}
              {Object.values(techRoutes).filter(r => r.visible && (r.routeSegments?.length ?? 0) === 0 && r.points.length > 1).map(route => (
                getSpeedSegments(route.points, route.techColor).map((seg, i) => (
                  <Polyline key={`rtf-${route.techId}-${i}`} positions={seg.positions} pathOptions={{ color: seg.color, weight: 4, opacity: 0.85 }} />
                ))
              ))}

              {/* Direction arrows along routes */}
              {Object.values(techRoutes).filter(r => r.visible && r.points.length > 2).map(route =>
                getDirectionArrows(route.points, route.techColor).map((arrow, i) => (
                  <Marker key={`arr-${route.techId}-${i}`} position={[arrow.lat, arrow.lng]}
                    icon={L.divIcon({
                      className: 'cm',
                      iconSize: [16, 16],
                      iconAnchor: [8, 8],
                      html: `<div style="width:16px;height:16px;display:flex;align-items:center;justify-content:center;transform:rotate(${arrow.angle - 90}deg)"><svg width="14" height="14" viewBox="0 0 24 24" fill="${route.techColor || '#1976d2'}" stroke="white" stroke-width="1.5"><path d="M5 12h14M13 5l7 7-7 7"/></svg></div>`
                    })}
                    interactive={false}
                  />
                ))
              )}

              {/* Flight segments - dashed arc + airplane markers */}
              {Object.values(techRoutes).filter(r => r.visible && r.flightSegments?.length > 0).map(route =>
                route.flightSegments.map((flight, fi) => (
                  <span key={`fl-${route.techId}-${fi}`}>
                    {/* Dashed flight trail */}
                    <Polyline
                      positions={flight.trail.map((p: FlightPoint) => [p.lat, p.lng] as [number, number])}
                      pathOptions={{ color: '#1565c0', weight: 3, opacity: 0.7, dashArray: '10 8' }}
                    >
                      <Popup maxWidth={260}>
                        <div style={{ fontSize: 11, lineHeight: 1.6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                            <span style={{ fontSize: 16 }}>\u2708</span>
                            <strong style={{ fontSize: 13 }}>Flight Detected</strong>
                          </div>
                          <div style={{ padding: '4px 6px', background: '#e3f2fd', borderRadius: 4 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Duration:</span><strong>{flight.duration_min} min</strong></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Distance:</span><strong>{fmtDistanceMiles(flight.distance_miles, unitsDistance, 1)}</strong></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Max Speed:</span><strong>{fmtSpeedMph(flight.max_speed_mph, unitsDistance, 0)}</strong></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Avg Speed:</span><span>{fmtSpeedMph(flight.avg_speed_mph, unitsDistance, 0)}</span></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Max Alt:</span><strong>{fmtAltFt(flight.max_altitude_ft, unitsSmall)}</strong></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Avg Alt:</span><span>{fmtAltFt(flight.avg_altitude_ft, unitsSmall)}</span></div>
                          </div>
                          <div style={{ marginTop: 4, fontSize: 10, color: '#666' }}>
                            {new Date(flight.takeoff.time).toLocaleTimeString()} → {new Date(flight.landing.time).toLocaleTimeString()}
                          </div>
                        </div>
                      </Popup>
                    </Polyline>
                    {/* Takeoff marker */}
                    <Marker position={[flight.takeoff.lat, flight.takeoff.lng]}
                      icon={L.divIcon({ className: 'cm', html: '<div style="font-size:18px;filter:drop-shadow(0 1px 2px rgba(0,0,0,.4))">\u2708\uFE0F</div>', iconSize: [24,24], iconAnchor: [12,12] })}>
                      <Popup><div style={{ fontSize: 12 }}><strong>\u2708 Takeoff</strong><br/>{new Date(flight.takeoff.time).toLocaleTimeString()}<br/>Max: {fmtAltFt(flight.max_altitude_ft, unitsSmall)} / {fmtSpeedMph(flight.max_speed_mph, unitsDistance, 0)}</div></Popup>
                    </Marker>
                    {/* Landing marker */}
                    <Marker position={[flight.landing.lat, flight.landing.lng]}
                      icon={L.divIcon({ className: 'cm', html: '<div style="font-size:18px;filter:drop-shadow(0 1px 2px rgba(0,0,0,.4))">\uD83D\uDEEC</div>', iconSize: [24,24], iconAnchor: [12,12] })}>
                      <Popup><div style={{ fontSize: 12 }}><strong>\uD83D\uDEEC Landing</strong><br/>{new Date(flight.landing.time).toLocaleTimeString()}<br/>Distance: {fmtDistanceMiles(flight.distance_miles, unitsDistance, 1)} in {flight.duration_min} min</div></Popup>
                    </Marker>
                    {/* Clickable altitude points along flight */}
                    {flight.trail.filter((_: FlightPoint, i: number) => i % Math.max(1, Math.floor(flight.trail.length / 15)) === 0).map((p: FlightPoint, i: number) => (
                      <LeafletCircle key={`fp-${route.techId}-${fi}-${i}`} center={[p.lat, p.lng]} radius={4}
                        pathOptions={{ color: '#1565c0', fillColor: '#42a5f5', fillOpacity: 0.9, weight: 1 }}>
                        <Popup maxWidth={160}>
                          <div style={{ fontSize: 11 }}>
                            <strong style={{ color: '#1565c0' }}>{fmtAltFt(p.altitude_ft, unitsSmall)}</strong><br/>
                            {fmtSpeedMph(p.speed_mph, unitsDistance, 0)} / {p.heading.toFixed(0)}\u00B0<br/>
                            <span style={{ color: '#666' }}>{new Date(p.timestamp).toLocaleTimeString()}</span>
                          </div>
                        </Popup>
                      </LeafletCircle>
                    ))}
                  </span>
                ))
              )}



              {/* Route start/end markers */}
              {Object.values(techRoutes).filter(r => r.visible && r.points.length > 0).map(route => (
                <span key={`se-${route.techId}`}>
                  <Marker position={[route.points[0].lat, route.points[0].lng]}
                    icon={L.divIcon({ className: 'cm', html: '<div style="width:14px;height:14px;background:#4caf50;border:3px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,.3)"></div>', iconSize: [14,14], iconAnchor: [7,7] })}>
                    <Popup><span style={{ fontSize: 12 }}>Start: {new Date(route.points[0].timestamp).toLocaleTimeString()}</span></Popup>
                  </Marker>
                  <Marker position={[route.points[route.points.length-1].lat, route.points[route.points.length-1].lng]}
                    icon={L.divIcon({ className: 'cm', html: '<div style="width:14px;height:14px;background:#f44336;border:3px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,.3)"></div>', iconSize: [14,14], iconAnchor: [7,7] })}>
                    <Popup><span style={{ fontSize: 12 }}>Last: {new Date(route.points[route.points.length-1].timestamp).toLocaleTimeString()}</span></Popup>
                  </Marker>
                </span>
              ))}

              {/* Directions route overlay */}
              {directionsRoute && (
                <Polyline positions={directionsRoute} pathOptions={{ color: '#1976d2', weight: 5, opacity: 0.8, dashArray: '12 6' }} />
              )}
              {directionsTarget && (
                <Marker position={[directionsTarget.lat, directionsTarget.lng]}
                  icon={L.divIcon({ className: 'cm', iconSize: [28, 28], iconAnchor: [14, 28], popupAnchor: [0, -28],
                    html: '<div style="font-size:28px;filter:drop-shadow(0 2px 3px rgba(0,0,0,.3))">\uD83D\uDCCD</div>' })}>
                  <Popup>
                    <div style={{ minWidth: 160, fontSize: 12 }}>
                      <strong>{directionsTarget.name}</strong><br/>
                      {directionsInfo && <span>{directionsInfo.distance} • {directionsInfo.duration}</span>}<br/>
                      <button onClick={() => openInMaps(directionsTarget.lat, directionsTarget.lng)} style={{ marginTop: 4, padding: '4px 10px', border: 'none', background: '#1976d2', color: 'white', borderRadius: 4, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                        Navigate ↗
                      </button>
                    </div>
                  </Popup>
                </Marker>
              )}

              {/* Stop markers */}
              {Object.values(techRoutes).filter(r => r.visible).map(route =>
                route.stops.map((stop, i) => (
                  <Marker key={`s-${route.techId}-${i}`} position={[stop.lat, stop.lng]} icon={createStopIcon(stop.stop_type === 'work', stop.duration_minutes, stop.order)}>
                    <Popup maxWidth={300}>
                      <div style={{ minWidth: 220 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <span style={{ background: stop.stop_type === 'work' ? '#7b1fa2' : '#e65100', color: '#fff', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{stop.order}</span>
                          <strong style={{ color: stop.stop_type === 'work' ? '#7b1fa2' : '#e65100', fontSize: 14 }}>
                            {stop.stop_type === 'work' ? '🔧 Job Stop' : '📍 Personal Stop'}
                          </strong>
                        </div>
                        <div style={{ fontSize: 12, marginBottom: 2 }}>
                          {new Date(stop.start).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})} — {new Date(stop.end).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}
                          <strong style={{ marginLeft: 6 }}>{stop.duration_minutes} min</strong>
                        </div>
                        {stop.notes && <div style={{ fontSize: 11, color: '#555', fontStyle: 'italic', margin: '4px 0' }}>📝 {stop.notes}</div>}
                        {stop.nearby_ticket && (
                          <div style={{ marginTop: 6, padding: 8, background: '#f3e5f5', borderRadius: 6, borderLeft: '3px solid #7b1fa2' }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#7b1fa2' }}>{stop.nearby_ticket.ticket_number}</div>
                            <div style={{ fontSize: 12, marginTop: 2 }}>{stop.nearby_ticket.title}</div>
                            <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{stop.nearby_ticket.distance_m}m away • {stop.nearby_ticket.status}</div>
                            <button onClick={() => navigate('/tickets?id=' + stop.nearby_ticket!.id)} style={{ marginTop: 6, padding: '4px 12px', cursor: 'pointer', border: 'none', background: '#7b1fa2', color: 'white', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>Open Ticket →</button>
                            <button onClick={() => openNavigation(stop.lat, stop.lng)} style={{ marginTop: 4, marginLeft: 4, padding: '4px 12px', cursor: 'pointer', border: 'none', background: '#1976d2', color: 'white', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>Navigate 🧭</button>
                          </div>
                        )}
                        {!stop.nearby_ticket && stop.stop_type !== 'work' && (
                          <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>No ticket matched at this location</div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                ))
              )}


            </MapContainer>

            {/* ── RECENTER / RESUME FOLLOW ──────────────────────────────────────
                Appears only when follow is armed AND the dispatcher has panned away
                by hand. Tapping it re-arms follow and flies back to the tech. This is
                the standard nav-app contract: your gesture always wins, and getting
                follow back is always one tap away. */}
            {followArmed && followPaused && (
              <Button
                onClick={handleRecenter}
                variant="contained"
                startIcon={<FollowIcon />}
                sx={{
                  position: 'absolute', zIndex: 650,
                  bottom: { xs: 76, md: 28 }, left: '50%', transform: 'translateX(-50%)',
                  borderRadius: 999, textTransform: 'none', fontWeight: 700, fontSize: 13,
                  px: 2.25, py: 0.9, boxShadow: '0 4px 16px rgba(0,0,0,.28)',
                  bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' },
                }}
              >
                Recenter
              </Button>
            )}

            {/* Layers panel overlay */}
            {layersPanelOpen && (
              <Paper elevation={3} sx={{ position: 'absolute', top: 10, left: 10, zIndex: 600, borderRadius: 2, width: { xs: 'calc(100% - 20px)', md: 240 }, maxHeight: 'calc(100% - 20px)', overflow: 'auto',
                '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#ccc', borderRadius: 2 } }}>
                <Box sx={{ p: 1.5, borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, bgcolor: 'white', zIndex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>Basemap, Weather & Layers</Typography>
                  <IconButton size="small" onClick={() => setLayersPanelOpen(false)}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                </Box>

                {/* ── BASEMAP SWITCHER ─────────────────────────────────────────
                    Driven entirely by /map-style/layers.json. Anything with
                    available:false renders disabled with its reason on hover — so the
                    UI tells the truth about what is and isn't built yet, instead of
                    offering a button that does nothing. */}
                {layersManifest && (
                  <Box sx={{ px: 1.5, pt: 1.5 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      BASEMAP
                    </Typography>
                    <ToggleButtonGroup
                      size="small" exclusive fullWidth value={activeBasemap}
                      onChange={(_e, v) => { if (v) pickBasemap(v); }}
                      sx={{ mb: 1.25 }}
                    >
                      {layersManifest.basemaps.map(b => (
                        <ToggleButton
                          key={b.id} value={b.id} disabled={!b.available}
                          title={b.available ? (b.description || b.label) : (b.description || 'Not available yet')}
                          sx={{ textTransform: 'none', fontSize: 12, py: 0.4 }}
                        >
                          {b.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>

                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                      OVERLAYS
                    </Typography>
                    {layersManifest.overlays.map(o => (
                      <Box key={o.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                           title={o.description || o.label}>
                        <Typography variant="body2" sx={{ fontSize: 12.5, color: o.available ? 'text.primary' : 'text.disabled' }}>
                          {o.label}
                        </Typography>
                        <Switch
                          size="small" disabled={!o.available}
                          checked={activeOverlays.includes(o.id)}
                          onChange={() => toggleOverlay(o.id)}
                        />
                      </Box>
                    ))}

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.25 }}
                         title="Dark map for night shifts — applied as a paint patch, no reload">
                      <Typography variant="body2" sx={{ fontSize: 12.5 }}>Night mode</Typography>
                      <Switch size="small" checked={mapTheme === 'night'} onChange={toggleTheme} />
                    </Box>
                    <Divider sx={{ my: 1.25 }} />
                  </Box>
                )}

                <Box sx={{ px: 1.5, pb: 1.5 }}>
                  {/* Current conditions */}
                  {weatherData && (
                    <Box sx={{ mb: 1.5, p: 1, bgcolor: '#f5f5f5', borderRadius: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>{weatherData.temp_f?.toFixed(0)}°F</Typography>
                        <Typography variant="caption" color="text.secondary">Feels {weatherData.feels_like_f?.toFixed(0)}°F</Typography>
                      </Box>
                      <Typography variant="caption" display="block" sx={{ textTransform: 'capitalize' }}>{weatherData.description}</Typography>
                      <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">💨 {fmtWindMph(weatherData.wind_speed_mph, unitsDistance)} {weatherData.wind_cardinal}</Typography>
                        {weatherData.wind_gust_mph > 0 && <Typography variant="caption" color="warning.main">gusts {fmtWindMph(weatherData.wind_gust_mph, unitsDistance)}</Typography>}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">💧 {weatherData.humidity}%</Typography>
                        <Typography variant="caption" color="text.secondary">👁 {fmtVisMi(weatherData.visibility_mi, unitsDistance)}</Typography>
                        <Typography variant="caption" color="text.secondary">🌡 {weatherData.pressure_hpa?.toFixed(0)} hPa</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.3, fontSize: '0.6rem' }}>
                        Source: {weatherData.source || 'ECMWF/GFS'}
                      </Typography>
                    </Box>
                  )}

                  {/* NWS Alerts */}
                  {nwsAlerts.length > 0 && (
                    <Box sx={{ mb: 1.5, p: 1, bgcolor: '#fff3e0', borderRadius: 1.5, border: '1px solid #ffe0b2' }}>
                      <Typography variant="caption" fontWeight={700} color="warning.main" display="block" sx={{ mb: 0.5 }}>
                        ⚠️ {nwsAlerts.length} Active Alert{nwsAlerts.length > 1 ? 's' : ''}
                      </Typography>
                      {nwsAlerts.map((a, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.2, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }, borderRadius: 0.5, px: 0.5 }}
                          onClick={() => setSelectedAlert(a)}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                            bgcolor: a.severity === 'Extreme' ? '#f44336' : a.severity === 'Severe' ? '#ff9800' : '#ffc107' }} />
                          <Typography variant="caption" sx={{ lineHeight: 1.3 }}>{a.event}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Layer toggles */}
                  <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5, color: '#555' }}>WEATHER LAYERS</Typography>
                  {[
                    { label: '🌧 Radar', value: radarOpacity, set: setRadarOpacity, defaultOn: 0.65 },
                    { label: '🌧 Precip', value: precipOpacity, set: setPrecipOpacity, defaultOn: 0.6 },
                    { label: '🌡 Temp', value: tempOpacity, set: setTempOpacity, defaultOn: 0.5 },
                    { label: '💨 Wind', value: windOpacity, set: setWindOpacity, defaultOn: 0.5 },
                    { label: '☁ Clouds', value: cloudsOpacity, set: setCloudsOpacity, defaultOn: 0.5 },
                    { label: '🔵 Pressure', value: pressureOpacity, set: setPressureOpacity, defaultOn: 0.5 },
                  ].map(layer => (
                    <Box key={layer.label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.3 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: layer.value > 0 ? 600 : 400, color: layer.value > 0 ? 'text.primary' : 'text.secondary', flex: 1 }}>
                        {layer.label}
                      </Typography>
                      <Switch size="small" checked={layer.value > 0}
                        onChange={(e) => layer.set(e.target.checked ? layer.defaultOn : 0)}
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#14548C' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#14548C' } }} />
                    </Box>
                  ))}

                  <Typography variant="caption" fontWeight={700} display="block" sx={{ mt: 1, mb: 0.5, pt: 1, borderTop: '1px solid #eee', color: '#555' }}>MAP LAYERS</Typography>
                  {[
                    { label: '🚗 Traffic (Google — 3rd party, off)', value: trafficOpacity, set: setTrafficOpacity, defaultOn: 0 },
                    { label: '⚠️ NWS Alerts', value: alertsOpacity, set: setAlertsOpacity, defaultOn: 0.7 },
                  ].map(layer => (
                    <Box key={layer.label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.3 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: layer.value > 0 ? 600 : 400, color: layer.value > 0 ? 'text.primary' : 'text.secondary', flex: 1 }}>
                        {layer.label}
                      </Typography>
                      <Switch size="small" checked={layer.value > 0}
                        onChange={(e) => layer.set(e.target.checked ? layer.defaultOn : 0)}
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#14548C' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#14548C' } }} />
                    </Box>
                  ))}

                  {/* Speed legend */}
                  <Typography variant="caption" fontWeight={700} display="block" sx={{ mt: 1, pt: 1, borderTop: '1px solid #eee', mb: 0.5, color: '#555' }}>SPEED LEGEND</Typography>
                  {(() => {
                    const u = speedUnitLabel(unitsDistance);
                    const t1 = Math.round(speedValueMps(13.4112, unitsDistance));
                    const t2 = Math.round(speedValueMps(22.352, unitsDistance));
                    const t3 = Math.round(speedValueMps(29.0576, unitsDistance));
                    return [['Stopped', '#9e9e9e'], [`< ${t1} ${u}`, '#4caf50'], [`${t1}-${t2}`, '#ff9800'], [`${t2}-${t3}`, '#f44336'], [`${t3}+`, '#9c27b0']];
                  })().map(([label, color]) => (
                    <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.2 }}>
                      <Box sx={{ width: 18, height: 4, bgcolor: color, borderRadius: 2 }} />
                      <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>{label}</Typography>
                    </Box>
                  ))}

                  {/* Marker legend */}
                  <Typography variant="caption" fontWeight={700} display="block" sx={{ mt: 1, pt: 1, borderTop: '1px solid #eee', mb: 0.5, color: '#555' }}>MARKERS</Typography>
                  {[['Technician', '#14548C', '●'], ['Urgent Job', '#f44336', '◆'], ['High Job', '#ff9800', '◆'], ['Normal Job', '#2196f3', '◆'], ['Low Job', '#4caf50', '◆'],
                    ['Job Stop', '#9c27b0', '●'], ['Other Stop', '#ff9800', '●'], ['Start', '#4caf50', '●'], ['End', '#f44336', '●']
                  ].map(([l, cl, s]) => (
                    <Box key={l as string} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.1 }}>
                      <span style={{ color: cl as string, fontSize: 11, width: 14, textAlign: 'center' }}>{s}</span>
                      <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>{l}</Typography>
                    </Box>
                  ))}
                  {/* Map Tile Settings */}
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #eee' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', mb: 0.5 }}
                      onClick={() => setMapSettingsOpen(!mapSettingsOpen)}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary">Map Tiles (Admin)</Typography>
                      <Typography variant="caption" color="primary">{mapSettingsOpen ? '\u25B2' : '\u25BC'}</Typography>
                    </Box>
                    {mapSettingsOpen && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ fontSize: 10 }}>Default</Typography>
                          <select value={mapTileSettings.default_tile} onChange={(e) => trackingApi.updateMapSettings({ map_default_tile: e.target.value }).then(() => trackingApi.getMapSettings().then(r => setMapTileSettings(r.data)))}
                            style={{ fontSize: 10, padding: '2px 4px', border: '1px solid #ccc', borderRadius: 3 }}>
                            <option value="carto_voyager">Detailed (CARTO)</option>
                            <option value="osm">Street (OSM)</option>
                            <option value="esri_satellite">Satellite (Esri)</option>
                            <option value="carto_dark">Dark (CARTO)</option>
                            <option value="opentopomap">Topo</option>
                            {mapTileSettings?.google_enabled && <option value="google_hybrid">Hybrid (Google)</option>}
                          </select>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" sx={{ fontSize: 10 }}>Google Tiles</Typography>
                          <input type="checkbox" checked={mapTileSettings?.google_enabled}
                            onChange={(e) => trackingApi.updateMapSettings({ map_google_tiles_enabled: e.target.checked ? 'true' : 'false' }).then(() => trackingApi.getMapSettings().then(r => setMapTileSettings(r.data)))}
                            style={{ width: 14, height: 14 }} />
                        </Box>
                        <Typography variant="caption" sx={{ fontSize: 9, color: '#999' }}>
                          {mapTileSettings?.google_enabled ? '* Google receives tile request IPs' : 'No data sent to Google'}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Map Tiles */}
                  {mapTileSettings && (
                    <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid #eee' }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Map Tiles</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontSize: 10 }}>Default tile (global)</Typography>
                        <select value={mapTileSettings.default_tile} onChange={async (e) => {
                          await trackingApi.updateMapSettings({ map_default_tile: e.target.value });
                          setMapTileSettings((p: any) => ({ ...p, default_tile: e.target.value, effective_tile: e.target.value }));
                        }} style={{ fontSize: 10, padding: '1px 4px', border: '1px solid #ccc', borderRadius: 3 }}>
                          {Object.entries(mapTileSettings.available_tiles || {}).map(([k, t]: [string, any]) => (
                            <option key={k} value={k}>{t.name}</option>
                          ))}
                        </select>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontSize: 10 }}>My preference</Typography>
                        <select value={mapTileSettings.user_preference || ''} onChange={async (e) => {
                          await trackingApi.updateMapSettings({ user_tile_preference: e.target.value || null });
                          setMapTileSettings((p: any) => ({ ...p, user_preference: e.target.value || null, effective_tile: e.target.value || p.default_tile }));
                        }} style={{ fontSize: 10, padding: '1px 4px', border: '1px solid #ccc', borderRadius: 3 }}>
                          <option value="">Use global default</option>
                          {Object.entries(mapTileSettings.available_tiles || {}).map(([k, t]: [string, any]) => (
                            <option key={k} value={k}>{t.name}</option>
                          ))}
                        </select>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ fontSize: 10 }}>Google tiles</Typography>
                        <input type="checkbox" checked={mapTileSettings.google_enabled} onChange={async (e) => {
                          await trackingApi.updateMapSettings({ map_google_tiles_enabled: e.target.checked ? 'true' : 'false' });
                          const r = await trackingApi.getMapSettings();
                          setMapTileSettings(r.data);
                        }} style={{ width: 14, height: 14 }} />
                      </Box>
                      <Typography variant="caption" sx={{ fontSize: 9, color: '#999', mt: 0.3, display: 'block' }}>Tile images only - no user data sent to Google</Typography>
                    </Box>
                  )}

                  {/* GPS Processing Settings */}
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #eee' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', mb: 0.5 }}
                      onClick={() => { setGpsSettingsOpen(!gpsSettingsOpen); if (!gpsSettingsOpen && Object.keys(gpsSettings).length === 0) loadGpsSettings(); }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary">GPS Processing</Typography>
                      <Typography variant="caption" color="primary">{gpsSettingsOpen ? '▲' : '▼'}</Typography>
                    </Box>
                    {gpsSettingsOpen && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                        {gpsSettingsLoading ? (
                          <Typography variant="caption" color="text.secondary">Loading...</Typography>
                        ) : (
                          <>
                            {/* v2.3.2 -- every route-pipeline knob, straight from the API's own
                                metadata (key/type/unit/min/max/desc). Numbers are shown AND typed in the
                                dispatcher's units; the value stored stays canonical. */}
                            {(gpsSettingsMeta || []).filter((x: any) => x.type === 'number').map((x: any) => {
                              const ul = rpUnitLabel(x.unit as RpUnit, unitsSmall, unitsDistance);
                              const shown = rpToDisplay(gpsSettings[x.key] ?? '', x.unit as RpUnit, unitsSmall, unitsDistance);
                              return (
                                <Box key={x.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }} title={x.desc || ''}>
                                  <Typography variant="caption" sx={{ fontSize: 10, flexShrink: 0 }}>
                                    {x.label}{ul ? ` (${ul})` : ''}
                                  </Typography>
                                  <input type="number" step="any" value={shown}
                                    data-testid={`map-rp-${x.key}`}
                                    onChange={(e) => {
                                      const stored = rpToStored(e.target.value, x.unit as RpUnit, unitsSmall, unitsDistance);
                                      const n = Number(stored);
                                      setGpsSettings(prev => ({ ...prev, [x.key]: stored }));
                                      if (stored !== '' && isFinite(n) &&
                                          (x.min == null || n >= x.min) && (x.max == null || n <= x.max)) {
                                        saveGpsSetting(x.key, String(n));
                                      } else if (stored !== '') {
                                        setSnackbar({ open: true, severity: 'error',
                                          message: `${x.label}: outside the safe range (${x.min}–${x.max} ${x.unit || ''}) — not saved` });
                                      }
                                    }}
                                    style={{ width: 66, padding: '2px 4px', fontSize: 11, border: '1px solid #ccc', borderRadius: 3, textAlign: 'right' }} />
                                </Box>
                              );
                            })}
                            {(gpsSettingsMeta || []).filter((x: any) => x.type === 'bool').map((x: any) => (
                              <Box key={x.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} title={x.desc || ''}>
                                <Typography variant="caption" sx={{ fontSize: 10 }}>{x.label}</Typography>
                                <input type="checkbox" checked={gpsSettings[x.key] === 'true'} onChange={(e) => saveGpsSetting(x.key, e.target.checked ? 'true' : 'false')}
                                  style={{ width: 14, height: 14 }} />
                              </Box>
                            ))}
                            {(gpsSettingsMeta || []).filter((x: any) => x.type === 'enum').map((x: any) => (
                              <Box key={x.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }} title={x.desc || ''}>
                                <Typography variant="caption" sx={{ fontSize: 10 }}>{x.label}</Typography>
                                <select value={gpsSettings[x.key] || x.default} onChange={(e) => saveGpsSetting(x.key, e.target.value)}
                                  style={{ fontSize: 10, padding: '1px 4px', border: '1px solid #ccc', borderRadius: 3 }}>
                                  {(x.values || []).map((v: string) => <option key={v} value={v}>{v}</option>)}
                                </select>
                              </Box>
                            ))}
                            <Typography variant="caption" sx={{ fontSize: 9, color: '#999', mt: 0.5 }}>Changes apply on next route load. Full editor: Devices → Route &amp; map pipeline.</Typography>
                          </>
                        )}
                      </Box>
                    )}
                  </Box>

                </Box>
              </Paper>
            )}

            {/* Route stats */}
            {Object.values(techRoutes).some(r => r.visible) && (
              <Paper elevation={2} sx={{ position: 'absolute', bottom: 10, left: 10, px: 1.5, py: 1, borderRadius: 2, bgcolor: 'rgba(255,255,255,.95)', zIndex: 500, maxWidth: 240, display: { xs: 'none', md: 'block' } }}>
                {Object.values(techRoutes).filter(r => r.visible).map(route => (
                  <Box key={route.techId} sx={{ mb: 0.5, '&:last-child': { mb: 0 } }}>
                    <Typography variant="caption" fontWeight={700} display="block">{route.techName} • {route.date}</Typography>
                    {route.flightSegments?.length > 0 && (
                      <Typography variant="caption" display="block" sx={{ color: '#1565c0', fontWeight: 600 }}>
                        \u2708 {route.flightSegments.length} flight{route.flightSegments.length > 1 ? 's' : ''}: {route.flightSegments.map((f: FlightSegment) => `${fmtDistanceMiles(f.distance_miles, unitsDistance, 1)}/${f.duration_min}min`).join(', ')}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {route.points.length} pts • {route.stops.length} stops • {route.stops.filter(s => s.nearby_ticket).length} jobs • max {fmtSpeedMps(route.maxSpeed, unitsDistance, 0)}
                    </Typography>
                    {route.clockEvents.map((e, i) => (
                      <Typography key={i} variant="caption" display="block" sx={{ color: e.type==='clock_in'?'success.main':'error.main' }}>
                        {e.type.replace('_',' ')}: {new Date(e.timestamp).toLocaleTimeString()}
                      </Typography>
                    ))}
                  </Box>
                ))}
              </Paper>
            )}
          </Box>
        </Box>
      </Box>

      {/* History dialog */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}><TimelineIcon color="primary" sx={{ mr: 1 }} />Load Route</DialogTitle>
        <DialogContent>
          {historyTechId && technicians.find(t => t.id === historyTechId) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2" fontWeight={600}>{technicians.find(t => t.id === historyTechId)?.name}</Typography>
              <TextField type="date" label="Date" value={historyDate} onChange={(e) => setHistoryDate(e.target.value)}
                InputLabelProps={{ shrink: true }} size="small" fullWidth />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)} size="small">Cancel</Button>
          <Button variant="contained" size="small" startIcon={<PlayIcon />}
            onClick={() => {
              const tech = technicians.find(t => t.id === historyTechId);
              if (tech) { loadRoute(tech, historyDate); setHistoryOpen(false); }
            }}>Load</Button>
        </DialogActions>
      </Dialog>

      {/* Alert detail dialog */}
      <Dialog open={!!selectedAlert} onClose={() => setSelectedAlert(null)} maxWidth="sm" fullWidth>
        {selectedAlert && (
          <>
            <DialogTitle sx={{ pb: 1, color: selectedAlert.severity === 'Extreme' ? 'error.main' : selectedAlert.severity === 'Severe' ? 'warning.main' : 'info.main' }}>
              ⚠️ {selectedAlert.event}
            </DialogTitle>
            <DialogContent>
              <Chip label={selectedAlert.severity} size="small" color={selectedAlert.severity === 'Extreme' ? 'error' : 'warning'} sx={{ mr: 1 }} />
              <Chip label={selectedAlert.urgency} size="small" variant="outlined" sx={{ mr: 1 }} />
              {selectedAlert.certainty && <Chip label={selectedAlert.certainty} size="small" variant="outlined" />}
              <Typography variant="body2" sx={{ mt: 2 }}>{selectedAlert.headline}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{selectedAlert.description}</Typography>
              {selectedAlert.instruction && (
                <Alert severity="warning" sx={{ mt: 2 }}>{selectedAlert.instruction}</Alert>
              )}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                Effective: {selectedAlert.effective ? new Date(selectedAlert.effective).toLocaleString() : 'N/A'}<br/>
                Expires: {selectedAlert.expires ? new Date(selectedAlert.expires).toLocaleString() : 'N/A'}<br/>
                Source: {selectedAlert.sender}<br/>
                Areas: {selectedAlert.areas}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedAlert(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Ticket Detail/Edit Dialog */}
      <Dialog open={ticketDialogOpen} onClose={() => setTicketDialogOpen(false)} maxWidth="sm" fullWidth>
        {selectedTicket && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
              <span style={{ color: priorityColors[selectedTicket.priority] || '#999', fontWeight: 700 }}>●</span>
              {selectedTicket.ticket_number} — {selectedTicket.title}
              <IconButton size="small" onClick={() => setTicketDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Title" fullWidth size="small" value={ticketForm.title || ''} onChange={e => setTicketForm({ ...ticketForm, title: e.target.value })} />
                <TextField label="Description" fullWidth size="small" multiline rows={3} value={ticketForm.description || ''} onChange={e => setTicketForm({ ...ticketForm, description: e.target.value })} />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={ticketForm.status || ''} label="Status" onChange={e => setTicketForm({ ...ticketForm, status: e.target.value })}>
                      {['new','unassigned','assigned','en_route','on_site','in_progress','pending_parts','completed','cancelled'].map(s => (
                        <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Priority</InputLabel>
                    <Select value={ticketForm.priority || ''} label="Priority" onChange={e => setTicketForm({ ...ticketForm, priority: e.target.value })}>
                      {['low','medium','high','urgent'].map(p => (
                        <MenuItem key={p} value={p}>{p}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Assigned To</InputLabel>
                    <Select value={ticketForm.assigned_to_id || ''} label="Assigned To" onChange={e => setTicketForm({ ...ticketForm, assigned_to_id: e.target.value })}>
                      <MenuItem value="">Unassigned</MenuItem>
                      {technicians.map(t => (<MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label="Scheduled Start" type="datetime-local" size="small" sx={{ flex: 1 }} InputLabelProps={{ shrink: true }} value={ticketForm.scheduled_start || ''} onChange={e => setTicketForm({ ...ticketForm, scheduled_start: e.target.value })} />
                  <TextField label="Scheduled End" type="datetime-local" size="small" sx={{ flex: 1 }} InputLabelProps={{ shrink: true }} value={ticketForm.scheduled_end || ''} onChange={e => setTicketForm({ ...ticketForm, scheduled_end: e.target.value })} />
                </Box>
                {selectedTicket.service_address_line1 && (
                  <Typography variant="body2" color="text.secondary">📍 {[selectedTicket.service_address_line1, selectedTicket.service_city, selectedTicket.service_state].filter(Boolean).join(', ')}</Typography>
                )}
                {selectedTicket.customer_name && <Typography variant="body2" color="text.secondary">🏢 {selectedTicket.customer_name}</Typography>}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setTicketDialogOpen(false); navigate(`/tickets?id=${selectedTicket.id}`); }}>Full Page</Button>
              <Button onClick={() => setTicketDialogOpen(false)}>Cancel</Button>
              <Button onClick={saveTicket} variant="contained" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({...snackbar, open: false})} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar({...snackbar, open: false})} severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>

      {isMobile && <Fab color="primary" size="medium" sx={{ position: 'fixed', bottom: 20, left: 20, zIndex: 1200 }} onClick={() => setMobileDrawerOpen(true)}><PeopleIcon /></Fab>}
      <SwipeableDrawer anchor="left" open={mobileDrawerOpen} onClose={() => setMobileDrawerOpen(false)} onOpen={() => setMobileDrawerOpen(true)}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: '80%', maxWidth: 300 } }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}><TechPanel /></Box>
      </SwipeableDrawer>
    </>
  );
}
